import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useBuyer } from "../contexts/BuyerContext";
import { useWeather, useBuyerProfile } from "./greeting/hooks";
import WeatherIconSVG from "./greeting/WeatherIconSVG";
import businessQuotes from "../data/businessQuotes.json";
import jmiLogoImg from "../assets/images/JMIlogo.png";

/**
 * JMI Dynamic Living Business Environment — Master City Layout
 *
 * Layout (Left to Right, Max 50% Building Height):
 * 1. Wide Luxury Mall / Jewellery Galleria showcasing Buyer Logo / Brand
 * 2. Modern Bus Stop with Digital Hoarding switching between Store Logo & JMI Logo
 * 3. JMI Tower with Direct crisp JMI Emblem
 * 4. Wide Connected Building showing pure floating Glowing JMI Text (no background)
 * 5. Upper 50%+ Open Celestial Sky: Flying Birds, Golden Private Jet, Sun/Moon, Stars & Clouds
 * 6. Road: JMI Cargo Hauler, Golden Sports Car, Two-Wheeler, Sedan, Night Police Patrol
 */

// --- Weather Classification Helpers ---
const isRainCode = (code) =>
  (code >= 51 && code <= 67) || (code >= 80 && code <= 82) || code >= 95;
const isSnowCode = (code) =>
  (code >= 71 && code <= 77) || (code >= 85 && code <= 86);
const isFogCode = (code) => code >= 45 && code <= 48;
const isStormCode = (code) => code >= 95;

const Greeting = () => {
  const { buyer } = useBuyer();
  const profile = useBuyerProfile(buyer?.buyerId);
  const weather = useWeather(buyer?.shippingLatitude, buyer?.shippingLongitude);

  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [isHovering, setIsHovering] = useState(false);

  // Time synchronizer (lightweight 5s interval)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Hover/pause detection — trigger after 2 seconds of no scroll
  useEffect(() => {
    let scrollTimeout;
    
    const handleScroll = () => {
      setIsHovering(false);
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsHovering(true);
      }, 2000);
    };
    
    const handleMouseEnter = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsHovering(true);
      }, 2000);
    };
    
    const handleMouseLeave = () => {
      setIsHovering(false);
      clearTimeout(scrollTimeout);
    };
    
    window.addEventListener('scroll', handleScroll);
    const cardEl = document.querySelector('.jmi-living-card');
    if (cardEl) {
      cardEl.addEventListener('mouseenter', handleMouseEnter);
      cardEl.addEventListener('mouseleave', handleMouseLeave);
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
      if (cardEl) {
        cardEl.removeEventListener('mouseenter', handleMouseEnter);
        cardEl.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  const hour = currentTime.getHours();

  // --- Real-time 4-Phase System ---
  const isMorning = hour >= 5 && hour < 12;
  const isAfternoon = hour >= 12 && hour < 17;
  const isEvening = hour >= 17 && hour < 20;
  const isNight = hour >= 20 || hour < 5;
  const isLateNight = hour >= 0 && hour < 5; // platinum theme subset of night

  const periodKey = isMorning
    ? "morning"
    : isAfternoon
    ? "afternoon"
    : isEvening
    ? "evening"
    : "night";

  // --- Weather Derived States ---
  const isRain = isRainCode(weather.code);
  const isSnow = isSnowCode(weather.code);
  const isFog = isFogCode(weather.code);
  const isStorm = isStormCode(weather.code);

  // --- Strict Environmental Rules ---
  const showJoggers = isMorning && !isRain && !isStorm;
  const showMorningCyclists = isMorning && !isRain && !isStorm;
  const showEveningCyclists = isEvening && !isRain && !isStorm;
  const showStreetLights = isEvening || isNight || isRain || isStorm;
  const showVehicleHeadlights = isEvening || isNight || isRain || isStorm;
  const showBuildingLights = isEvening || isNight;
  const showBirds = (isMorning || isAfternoon || isEvening) && !isRain && !isStorm;
  const showPlane = !isStorm;

  // Dynamic Greeting Text
  const greetingText = useMemo(() => {
    if (isMorning) return hour < 8 ? "Early Market Opening" : "Good Morning";
    if (isAfternoon) return "Good Afternoon";
    if (isEvening) return "Good Evening";
    if (hour >= 0 && hour < 4) return "Late Night Trading";
    return "Good Night";
  }, [isMorning, isAfternoon, isEvening, hour]);

  // Business Quotes selection
  const getBusinessQuote = useCallback(
    (index) => {
      const bucket = businessQuotes?.[periodKey] || [];
      if (!bucket.length) return null;
      return bucket[((index % bucket.length) + bucket.length) % bucket.length];
    },
    [periodKey]
  );

  useEffect(() => {
    const start = new Date(new Date().getFullYear(), 0, 0);
    const dayOfYear = Math.floor((new Date() - start) / (1000 * 60 * 60 * 24));
    setQuoteIndex(dayOfYear);
    setSelectedQuote(getBusinessQuote(dayOfYear));
  }, [periodKey, getBusinessQuote]);

  const handleRefreshQuote = () => {
    const nextIdx = quoteIndex + 1;
    setQuoteIndex(nextIdx);
    setSelectedQuote(getBusinessQuote(nextIdx));
  };

  const buyerName =
    profile?.organizationName ||
    buyer?.organizationName ||
    buyer?.name ||
    "JMI Trading Partner";

  const buyerLogo =
    profile?.logoUrl ||
    profile?.storeLogo ||
    buyer?.logoUrl ||
    buyer?.storeLogo ||
    profile?.logo ||
    buyer?.logo ||
    null;

  // Generate initials from buyer name (first letter of each word in CAPS)
  const buyerInitials = useMemo(() => {
    return buyerName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  }, [buyerName]);

  // Use initials if name is too long (more than 15 characters)
  const displayName = buyerName.length > 15 ? buyerInitials : buyerName.toUpperCase();

  const envTheme = useMemo(() => {
    // 1. MORNING (05:00 – 11:59) — BLUE SAPPHIRE
    if (isMorning) {
      if (isStorm) {
        return {
          sky: "linear-gradient(180deg, #09131f 0%, #17263c 45%, #2a3c54 100%)",
          sunGlow: "rgba(251, 191, 36, 0.2)",
          cloudFill: "rgba(100, 116, 139, 0.7)",
          bldgFar: "#15202e",
          bldgMid: "#1e2e42",
          bldgNear: "#2a3d54",
          roadColor: "#172230",
          roadMarking: "rgba(251, 191, 36, 0.4)",
          windowGlow: "rgba(253, 224, 71, 0.7)",
          lightOpacity: 0.5,
          showStars: false,
          showSun: false,
          showSunset: false,
          showMoon: false,
        };
      }
      if (isRain || isFog) {
        return {
          sky: "linear-gradient(180deg, #334155 0%, #475569 48%, #64748b 100%)",
          sunGlow: "rgba(253, 230, 138, 0.3)",
          cloudFill: "rgba(148, 163, 184, 0.75)",
          bldgFar: "#1e293b",
          bldgMid: "#334155",
          bldgNear: "#475569",
          roadColor: "#273444",
          roadMarking: "rgba(255, 255, 255, 0.5)",
          windowGlow: "rgba(254, 240, 138, 0.6)",
          lightOpacity: 0.4,
          showStars: false,
          showSun: false,
          showSunset: false,
          showMoon: false,
        };
      }
      if (isSnow) {
        return {
          sky: "linear-gradient(180deg, #94a3b8 0%, #cbd5e1 55%, #e2e8f0 100%)",
          sunGlow: "rgba(255, 255, 255, 0.4)",
          cloudFill: "rgba(241, 245, 249, 0.8)",
          bldgFar: "#334155",
          bldgMid: "#475569",
          bldgNear: "#64748b",
          roadColor: "#475569",
          roadMarking: "rgba(255, 255, 255, 0.6)",
          windowGlow: "rgba(254, 240, 138, 0.7)",
          lightOpacity: 0.3,
          showStars: false,
          showSun: true,
          showSunset: false,
          showMoon: false,
        };
      }
      // Clear sapphire morning sky
      return {
        sky: "linear-gradient(180deg, #0a1a4a 0%, #0f52ba 28%, #1e7cd4 55%, #38bdf8 80%, #fed7aa 100%)",
        sunGlow: "rgba(254, 215, 170, 0.85)",
        cloudFill: "rgba(186, 230, 253, 0.7)",
        bldgFar: "#071230",
        bldgMid: "#0f2050",
        bldgNear: "#163070",
        roadColor: "#0a1428",
        roadMarking: "rgba(91, 163, 245, 0.8)",
        windowGlow: "rgba(186, 230, 253, 0.4)",
        lightOpacity: 0,
        showStars: false,
        showSun: true,
        showSunset: false,
        showMoon: false,
      };
    }

    // 2. AFTERNOON/NOON (12:00 – 16:59) — GOLDEN
    if (isAfternoon) {
      if (isStorm || isRain) {
        return {
          sky: "linear-gradient(180deg, #1e293b 0%, #334155 52%, #475569 100%)",
          sunGlow: "rgba(251, 191, 36, 0.2)",
          cloudFill: "rgba(100, 116, 139, 0.85)",
          bldgFar: "#0f172a",
          bldgMid: "#1e293b",
          bldgNear: "#334155",
          roadColor: "#1a2433",
          roadMarking: "rgba(255, 255, 255, 0.6)",
          windowGlow: "rgba(253, 224, 71, 0.65)",
          lightOpacity: 0.3,
          showStars: false,
          showSun: false,
          showSunset: false,
          showMoon: false,
        };
      }
      if (isSnow) {
        return {
          sky: "linear-gradient(180deg, #64748b 0%, #94a3b8 50%, #cbd5e1 100%)",
          sunGlow: "rgba(255, 255, 255, 0.5)",
          cloudFill: "rgba(248, 250, 252, 0.8)",
          bldgFar: "#1e293b",
          bldgMid: "#334155",
          bldgNear: "#475569",
          roadColor: "#334155",
          roadMarking: "rgba(255, 255, 255, 0.7)",
          windowGlow: "rgba(253, 224, 71, 0.6)",
          lightOpacity: 0.2,
          showStars: false,
          showSun: true,
          showSunset: false,
          showMoon: false,
        };
      }
      // Clear golden noon sky
      return {
        sky: "linear-gradient(180deg, #1a0e00 0%, #7a4800 18%, #c97d00 40%, #f5a623 65%, #ffd700 82%, #fff4b0 100%)",
        sunGlow: "rgba(255, 215, 0, 0.98)",
        cloudFill: "rgba(255, 244, 176, 0.75)",
        bldgFar: "#2a1800",
        bldgMid: "#3d2400",
        bldgNear: "#573400",
        roadColor: "#2a1c00",
        roadMarking: "rgba(255, 215, 0, 0.9)",
        windowGlow: "rgba(255, 244, 176, 0.45)",
        lightOpacity: 0,
        showStars: false,
        showSun: true,
        showSunset: false,
        showMoon: false,
      };
    }

    // 3. EVENING (17:00 – 19:59) — RUBY
    if (isEvening) {
      if (isStorm || isRain) {
        return {
          sky: "linear-gradient(180deg, #1e1b4b 0%, #312e81 45%, #4c1d95 75%, #581c87 100%)",
          sunGlow: "rgba(244, 63, 94, 0.4)",
          cloudFill: "rgba(76, 29, 149, 0.7)",
          bldgFar: "#0b0f19",
          bldgMid: "#141d2e",
          bldgNear: "#1e293b",
          roadColor: "#111827",
          roadMarking: "rgba(224, 17, 95, 0.6)",
          windowGlow: "rgba(255, 107, 157, 0.85)",
          lightOpacity: 0.75,
          showStars: false,
          showSun: false,
          showSunset: true,
          showMoon: false,
        };
      }
      if (isSnow) {
        return {
          sky: "linear-gradient(180deg, #312e81 0%, #4338ca 40%, #7c3aed 75%, #f472b6 100%)",
          sunGlow: "rgba(251, 146, 60, 0.5)",
          cloudFill: "rgba(226, 232, 240, 0.7)",
          bldgFar: "#111827",
          bldgMid: "#1f2937",
          bldgNear: "#374151",
          roadColor: "#1f2937",
          roadMarking: "rgba(255, 255, 255, 0.6)",
          windowGlow: "rgba(255, 107, 157, 0.8)",
          lightOpacity: 0.8,
          showStars: false,
          showSun: false,
          showSunset: true,
          showMoon: false,
        };
      }
      // Clear ruby evening sky
      return {
        sky: "linear-gradient(180deg, #1a0008 0%, #5c001a 22%, #8b0032 42%, #c8003a 60%, #e0115f 75%, #ff6b9d 90%, #ffd0e0 100%)",
        sunGlow: "rgba(224, 17, 95, 0.85)",
        cloudFill: "rgba(255, 182, 193, 0.65)",
        bldgFar: "#120006",
        bldgMid: "#1f000e",
        bldgNear: "#2e0016",
        roadColor: "#0e0006",
        roadMarking: "rgba(224, 17, 95, 0.85)",
        windowGlow: "rgba(255, 107, 157, 0.9)",
        lightOpacity: 0.9,
        showStars: false,
        showSun: false,
        showSunset: true,
        showMoon: false,
      };
    }

    // 4. LATE NIGHT (00:00 – 04:59) — PLATINUM
    if (isLateNight) {
      if (isSnow) {
        return {
          sky: "linear-gradient(180deg, #0a0a0f 0%, #111118 40%, #1a1a26 75%, #242430 100%)",
          sunGlow: "none",
          cloudFill: "rgba(200, 200, 210, 0.3)",
          bldgFar: "#060608",
          bldgMid: "#0d0d12",
          bldgNear: "#141420",
          roadColor: "#1a1a26",
          roadMarking: "rgba(229, 228, 226, 0.6)",
          windowGlow: "rgba(229, 228, 226, 0.95)",
          lightOpacity: 1,
          showStars: true,
          showSun: false,
          showSunset: false,
          showMoon: true,
        };
      }
      return {
        sky: "linear-gradient(180deg, #06060a 0%, #0c0c14 35%, #141420 65%, #1c1c2c 100%)",
        sunGlow: "none",
        cloudFill: "rgba(180, 180, 195, 0.3)",
        bldgFar: "#040408",
        bldgMid: "#08080e",
        bldgNear: "#0e0e18",
        roadColor: "#070710",
        roadMarking: "rgba(229, 228, 226, 0.65)",
        windowGlow: "rgba(229, 228, 226, 0.92)",
        lightOpacity: 1,
        showStars: !isRain && !isStorm && !isFog,
        showSun: false,
        showSunset: false,
        showMoon: !isRain && !isStorm,
      };
    }

    // 5. NIGHT (20:00 – 23:59) — SILVER
    if (isSnow) {
      return {
        sky: "linear-gradient(180deg, #050b14 0%, #0c1626 40%, #17253b 75%, #243854 100%)",
        sunGlow: "none",
        cloudFill: "rgba(148, 163, 184, 0.3)",
        bldgFar: "#060911",
        bldgMid: "#0d131f",
        bldgNear: "#151e2e",
        roadColor: "#1e293b",
        roadMarking: "rgba(192, 192, 192, 0.6)",
        windowGlow: "rgba(192, 192, 192, 0.95)",
        lightOpacity: 1,
        showStars: true,
        showSun: false,
        showSunset: false,
        showMoon: true,
      };
    }
    return {
      sky: "linear-gradient(180deg, #050509 0%, #0a0e1a 32%, #0f1525 60%, #141e36 100%)",
      sunGlow: "none",
      cloudFill: "rgba(40, 44, 60, 0.5)",
      bldgFar: "#040408",
      bldgMid: "#080a14",
      bldgNear: "#0e1220",
      roadColor: "#08080e",
      roadMarking: "rgba(192, 192, 192, 0.65)",
      windowGlow: "rgba(192, 192, 192, 0.92)",
      lightOpacity: 1,
      showStars: !isRain && !isStorm && !isFog,
      showSun: false,
      showSunset: false,
      showMoon: !isRain && !isStorm,
    };
  }, [isMorning, isAfternoon, isEvening, isLateNight, isStorm, isRain, isFog, isSnow]);

  // Window matrix generator with colorful gem-themed windows
  const renderWindowGrid = (x, y, cols, rows, spacingX = 8, spacingY = 9, w = 4.5, h = 5) => {
    const nodes = [];
    
    // Gem theme window colors (SOLID colors, NOT gradients)
    let windowColors = [];
    if (isLateNight) {
      // PLATINUM theme — silver/white spectrum
      windowColors = ['#e5e4e2', '#f0efed', '#d1d1cf', '#c0c0c0', '#e8e8e6'];
    } else if (isNight && !isLateNight) {
      // SILVER theme — cool silvers and light grays
      windowColors = ['#c0c0c0', '#d8d8d8', '#b0b0b0', '#a8a8a8', '#e0e0e0'];
    } else if (isEvening) {
      // RUBY theme — reds, pinks, magentas
      windowColors = ['#e0115f', '#ff2d6b', '#c8003a', '#ff6b9d', '#d41159'];
    } else if (isAfternoon) {
      // GOLDEN theme — golds, yellows, ambers
      windowColors = ['#ffd700', '#ffcc00', '#f59e0b', '#fbbf24', '#d97706'];
    } else {
      // SAPPHIRE theme — blues, cyans, azure
      windowColors = ['#0f52ba', '#2e7dd1', '#1e90ff', '#5ba3f5', '#0ea5e9'];
    }
    
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const seed = (x * 13 + y * 7 + r * 5 + c * 3) % 10;
        let lit = false;
        if (isNight || isLateNight) lit = seed < 7;
        else if (isEvening) lit = seed < 8;
        else if (isMorning || isAfternoon) lit = seed < 4;
        
        // When hovering, ALL windows light up in colorful gem colors
        const colorIndex = (seed + r + c) % windowColors.length;
        const windowColor = isHovering ? windowColors[colorIndex] : (lit ? envTheme.windowGlow : "rgba(15, 23, 42, 0.6)");
        const windowOpacity = isHovering ? 0.85 : (lit ? (isNight ? 0.9 : 0.6) : 0.3);

        nodes.push(
          <rect
            key={`win-${x}-${y}-${r}-${c}`}
            x={x + c * spacingX}
            y={y + r * spacingY}
            width={w}
            height={h}
            fill={windowColor}
            opacity={windowOpacity}
            rx="0.5"
            style={isHovering ? { transition: 'fill 0.8s ease, opacity 0.8s ease' } : {}}
          />
        );
      }
    }
    return nodes;
  };

  return (
    <div className="card" >
      <div className="jmi-greeting-wrapper">
        <section
          className="jmi-living-card"
          role="banner"
          aria-label="JMI Living B2B Jewellery Ecosystem"
        >
        {/* Dynamic Sky Backdrop (Takes 50%+ of vertical card) */}
        <div
          className="jmi-sky-bg"
          style={{
            background: envTheme.sky,
            transition: "background 1.5s ease-in-out",
          }}
        />

        {/* Decorative Geometric Shapes Background Layer */}
        <div className="jmi-geometric-shapes" aria-hidden="true">
          <div className="jmi-shape jmi-circle-1" style={{ opacity: isNight || isEvening ? 0.15 : 0.08 }} />
          <div className="jmi-shape jmi-circle-2" style={{ opacity: isNight || isEvening ? 0.12 : 0.06 }} />
          <div className="jmi-shape jmi-triangle-1" style={{ opacity: isNight || isEvening ? 0.1 : 0.05 }} />
          <div className="jmi-shape jmi-square-1" style={{ opacity: isNight || isEvening ? 0.13 : 0.07 }} />
          <div className="jmi-shape jmi-hexagon-1" style={{ opacity: isNight || isEvening ? 0.11 : 0.06 }} />
        </div>

        {/* Stars in Clear Evening/Night Sky */}
        {envTheme.showStars && (
          <div className="jmi-stars-layer">
            {[
              { x: 5, y: 12, s: 2.2, d: 0.2 },
              { x: 14, y: 6, s: 1.5, d: 1.1 },
              { x: 22, y: 18, s: 2.8, d: 0.6 },
              { x: 31, y: 9, s: 1.8, d: 1.8 },
              { x: 42, y: 15, s: 2.5, d: 0.9 },
              { x: 53, y: 7, s: 1.6, d: 2.1 },
              { x: 64, y: 14, s: 2.4, d: 1.4 },
              { x: 75, y: 8, s: 1.8, d: 0.4 },
              { x: 86, y: 16, s: 2.6, d: 1.9 },
              { x: 94, y: 10, s: 2.0, d: 0.8 },
              { x: 18, y: 28, s: 1.4, d: 1.3 },
              { x: 48, y: 25, s: 1.6, d: 0.7 },
              { x: 79, y: 24, s: 1.5, d: 2.4 },
            ].map((star, idx) => (
              <span
                key={idx}
                className="jmi-diamond-star"
                style={{
                  left: `${star.x}%`,
                  top: `${star.y}%`,
                  width: `${star.s}px`,
                  height: `${star.s}px`,
                  animationDelay: `${star.d}s`,
                }}
              />
            ))}
          </div>
        )}

        {/* Celestial Sun / Sunset Glow / Moon */}
        {envTheme.showSun && (
          <div
            className="jmi-celestial-sun"
            style={{
              top: isMorning ? "18px" : "10px",
              right: isMorning ? "22%" : "12%",
              background: envTheme.sunGlow,
            }}
          >
            <div className="jmi-sun-core" />
          </div>
        )}

        {envTheme.showSunset && (
          <div
            className="jmi-sunset-glow"
            style={{ background: envTheme.sunGlow }}
          />
        )}

        {envTheme.showMoon && (
          <div className="jmi-celestial-moon">
            <div className="jmi-moon-crater c1" />
            <div className="jmi-moon-crater c2" />
          </div>
        )}

        {/* Dynamic Slow Cloud Systems */}
        <svg
          className="jmi-clouds-svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <g fill={envTheme.cloudFill} className="jmi-clouds-drift">
            <path d="M 30,55 Q 60,30 95,42 Q 125,20 160,38 Q 190,32 210,55 Z" />
            <path d="M 380,45 Q 410,18 450,30 Q 485,15 520,32 Q 550,25 575,48 Z" />
            <path d="M 740,58 Q 770,28 810,40 Q 845,18 885,38 Q 915,30 945,58 Z" />
            <path d="M 1040,42 Q 1070,20 1105,32 Q 1135,16 1165,34 Q 1185,28 1200,45 Z" />
          </g>
        </svg>

        {/* Weather Precipitation Layer */}
        {isRain && (
          <div className="jmi-weather-layer" aria-hidden="true">
            {Array.from({ length: 36 }).map((_, i) => (
              <div
                key={i}
                className="jmi-rain-streak"
                style={{
                  left: `${(i * 2.8) % 100}%`,
                  animationDelay: `${(i * 0.08) % 0.8}s`,
                  animationDuration: isStorm ? "0.32s" : "0.55s",
                }}
              />
            ))}
          </div>
        )}

        {isSnow && (
          <div className="jmi-weather-layer" aria-hidden="true">
            {Array.from({ length: 28 }).map((_, i) => (
              <div
                key={i}
                className="jmi-snow-particle"
                style={{
                  left: `${(i * 3.6) % 100}%`,
                  animationDelay: `${(i * 0.22) % 2.8}s`,
                  animationDuration: `${2.8 + (i % 4) * 0.4}s`,
                  fontSize: `${8 + (i % 3) * 3}px`,
                }}
              >
                ❄
              </div>
            ))}
          </div>
        )}

        {isStorm && <div className="jmi-lightning-pulse" aria-hidden="true" />}
        {isFog && <div className="jmi-fog-blanket" aria-hidden="true" />}

        {/* Jewellery Sparkles */}
        {(isNight || isEvening || (!isRain && isMorning)) && (
          <div className="jmi-sparkle-layer" aria-hidden="true">
            <span className="jmi-sparkle-glint s1">✦</span>
            <span className="jmi-sparkle-glint s2">✦</span>
            <span className="jmi-sparkle-glint s3">✦</span>
            <span className="jmi-sparkle-glint s4">✦</span>
          </div>
        )}

        {/* Vector Cityscape, Buildings (Max 50% height), Entities & Road SVG */}
        <svg
          className="jmi-cityscape-svg"
          viewBox="0 0 1200 240"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="jmiLogoGoldGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#fef08a" />
              <stop offset="50%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#d97706" />
            </linearGradient>
            <linearGradient id="jmiGoldenPlaneGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#fef08a" />
              <stop offset="60%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#b45309" />
            </linearGradient>
            <linearGradient id="jmiSportsCarGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#dc2626" />
              <stop offset="45%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#ef4444" />
            </linearGradient>
            <linearGradient id="jmiRedNeonGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="45%" stopColor="#fca5a5" />
              <stop offset="100%" stopColor="#ef4444" />
            </linearGradient>
            <linearGradient id="jmiGlassFacade" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1e3a8a" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#0f172a" stopOpacity="0.95" />
            </linearGradient>
            <linearGradient id="jmiMallFacade" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1e293b" />
              <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
            <linearGradient id="jmiStreetLampBeam" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fef08a" stopOpacity="0.8" />
              <stop offset="70%" stopColor="#fde047" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="jmiHeadlightBeam" x1="1" y1="0.5" x2="0" y2="0.5">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.85" />
              <stop offset="35%" stopColor="#fef08a" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#fef08a" stopOpacity="0" />
            </linearGradient>
            {/* Road-projected angled beam gradients — fan shape hitting tarmac */}
            <linearGradient id="jmiRoadBeamWhite" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.7" />
              <stop offset="60%" stopColor="#fef9c3" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#fef9c3" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="jmiRoadBeamGold" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#ffd700" stopOpacity="0.75" />
              <stop offset="55%" stopColor="#f59e0b" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="jmiRoadBeamBlue" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#93c5fd" stopOpacity="0.7" />
              <stop offset="55%" stopColor="#3b82f6" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </linearGradient>
            <filter id="jmiGlow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="jmiPureNeonGlow">
              <feGaussianBlur stdDeviation="4" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* ========================================================================= */}
          {/* LAYER 0: SKY ENTITIES — BIRDS & GOLDEN PRIVATE PLANE                      */}
          {/* ========================================================================= */}

          {/* Flying Birds Flock (Morning / Afternoon / Evening) */}
          {showBirds && (
            <g className="jmi-birds-flock" transform="scale(1.8)">
              {/* Bird 1 - Gemstone Pink */}
              <g className="jmi-bird-unit" transform="translate(0, 0)">
                {/* Gemstone sparkle on bird body */}
                <circle cx="0" cy="0" r="3.2" fill="#ec4899" opacity="0.9" />
                <circle cx="0" cy="0" r="2" fill="#f472b6" opacity="0.95" />
                <circle cx="-1" cy="-1.5" r="1.2" fill="#ffffff" opacity="0.7" />
              </g>
              {/* Bird 2 - Gemstone Blue */}
              <g className="jmi-bird-unit" transform="translate(25, -12)">
                {/* Gemstone sparkle - diamond blue */}
                <circle cx="0" cy="0" r="2.8" fill="#0ea5e9" opacity="0.9" />
                <circle cx="0" cy="0" r="1.6" fill="#7dd3fc" opacity="0.95" />
                <circle cx="1" cy="-1.2" r="1" fill="#ffffff" opacity="0.7" />
              </g>
              {/* Bird 3 - Gemstone Green */}
              <g className="jmi-bird-unit" transform="translate(48, -6)">
                {/* Gemstone sparkle - emerald green */}
                <circle cx="0" cy="0" r="2.5" fill="#10b981" opacity="0.9" />
                <circle cx="0" cy="0" r="1.4" fill="#6ee7b7" opacity="0.95" />
                <circle cx="-0.8" cy="-1" r="0.9" fill="#ffffff" opacity="0.7" />
              </g>
              {/* Bird 4 - Gemstone Gold */}
              <g className="jmi-bird-unit" transform="translate(70, -18)">
                {/* Gemstone sparkle - ruby red */}
                <circle cx="0" cy="0" r="2.3" fill="#f59e0b" opacity="0.9" />
                <circle cx="0" cy="0" r="1.3" fill="#fcd34d" opacity="0.95" />
                <circle cx="1.2" cy="-0.8" r="0.8" fill="#ffffff" opacity="0.7" />
              </g>
            </g>
          )}

          {/* Realistic Commercial Plane #1 - Metallic/Silver with Gold Tail (First Design) */}
          {showPlane && (
            <g className="jmi-commercial-plane-1">
              
              {/* Far Side Wing */}
              <path d="M 33,17 L 43,15 L 41.5,16.5 L 34,18 Z" fill="#6C7A8C" />
              
              {/* Tail Fin (Vertical Stabilizer) with Gold Geometric Pattern */}
              <g>
                <path d="M 41.5,17.5 L 53,6 L 58,7 L 50,17 Z" fill="#1E2A38" />
                <polygon points="53,6 58,7 56,10 52,9" fill="#D4AF37" />
                <polygon points="52,9 56,10 54,13 50,11" fill="#C59B27" />
                <polygon points="50,11 54,13 52,16 49,14" fill="#7A5C10" />
                <polygon points="53,6 52,9 50,11 49,8" fill="#997519" />
                {/* JMI Logo on Tail */}
                <text x="52" y="12" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="2.2" fill="#FFFFFF" transform="rotate(-30 52 12) skewX(-10)" opacity="0.95" letterSpacing="0.35">JMI</text>
              </g>
              
              {/* Horizontal Stabilizer */}
              <path d="M 49,17 L 56,16.8 L 54.5,18 L 47.5,18.2 Z" fill="#4B5666" />
              
              {/* Main Fuselage Body - Metallic Gradient */}
              <defs>
                <linearGradient id="fuselageGradPlane1" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#FFFFFF"/>
                  <stop offset="20%" stopColor="#E2E7ED"/>
                  <stop offset="50%" stopColor="#C5CFDB"/>
                  <stop offset="85%" stopColor="#9AA8B9"/>
                  <stop offset="100%" stopColor="#69788A"/>
                </linearGradient>
              </defs>
              <path d="M 5.5,17 C 5.5,15.5 10,14.5 19,15 L 46,16.5 C 50,17 51.5,17.5 51.5,17.8 C 51.5,18.2 48,19 42.5,19.2 L 19,19.5 C 11,19.5 5.5,18.5 5.5,17 Z" fill="url(#fuselageGradPlane1)" />
              
              {/* Cockpit Windows */}
              <path d="M 6.5,16.5 C 7,16 8.5,15.8 9.5,15.9 L 9.6,16.5 C 8.5,16.4 7,16.7 6.6,17 Z" fill="#1A2530" />
              
              {/* Passenger Windows */}
              <line x1="11" y1="15.5" x2="41" y2="16.5" stroke="#2C3E50" strokeWidth="0.15" strokeDasharray="0.25,0.25" opacity="0.7"/>
              <line x1="10" y1="16.3" x2="42.5" y2="17.2" stroke="#2C3E50" strokeWidth="0.2" strokeDasharray="0.25,0.25" opacity="0.8"/>
              
              {/* Main "JMI" Text on Fuselage - sized to body height */}
              <defs>
                <linearGradient id="goldGradPlane1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#D4AF37"/>
                  <stop offset="50%" stopColor="#AA7C11"/>
                  <stop offset="100%" stopColor="#5B4206"/>
                </linearGradient>
              </defs>
              <g transform="translate(12.5, 16.5) skewX(-12) rotate(-1)">
                <text x="0" y="0" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="2.8" fill="#000000" opacity="0.15">JMI</text>
                <text x="-0.08" y="-0.08" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="2.8" fill="url(#goldGradPlane1)" letterSpacing="0.5">JMI</text>
                <rect x="0" y="0.4" width="6" height="0.15" fill="#D4AF37" />
              </g>
              
              {/* Near Side Wing */}
              <path d="M 18,18.2 L 35,19 L 33.5,19.8 L 21.5,19.2 Z" fill="#8493A6" />
              
              {/* Winglet */}
              <path d="M 35,19 L 35.5,18 L 35,19.2 L 33.5,19.8 Z" fill="#D4AF37" />
              
              {/* Engines */}
              <g>
                <rect x="17.5" y="18.5" width="3.5" height="1.7" rx="0.8" fill="#A3B1C2" stroke="#4A5568" strokeWidth="0.08" />
                <ellipse cx="17.5" cy="19.3" rx="0.3" ry="0.85" fill="#1A202C" />
              </g>
              <g>
                <rect x="14.5" y="18.2" width="3" height="1.5" rx="0.7" fill="#A3B1C2" stroke="#4A5568" strokeWidth="0.08" />
                <ellipse cx="14.5" cy="18.9" rx="0.28" ry="0.75" fill="#1A202C" />
              </g>
              
              {/* Belly Highlight */}
              <path d="M 9.5,18 C 14,19 27.5,19.2 39.5,18.5 C 42.5,18.3 45,18 45,18 C 45,18 42,19.2 37,19.3 C 25,19.6 13,19 9.5,18 Z" fill="#4A5568" opacity="0.5" />
              
              {/* Navigation Lights */}
              <circle cx="35.5" cy="18" r="0.3" fill="#ef4444" opacity={isNight || isEvening ? "1" : "0.6"} filter={isNight || isEvening ? "url(#jmiGlow)" : undefined} />
              <circle cx="33.5" cy="19.8" r="0.3" fill="#10b981" opacity={isNight || isEvening ? "1" : "0.6"} filter={isNight || isEvening ? "url(#jmiGlow)" : undefined} />
            </g>
          )}

          {/* Realistic Commercial Plane #2 - White/Gold Luxury Livery (Second Design) */}
          {showPlane && (
            <g className="jmi-commercial-plane-2">
              
              {/* Far-Side Wing with Gold Tip */}
              <path d="M 43,39 L 49,34 L 51,35 L 45.5,40 Z" fill="#D9E2EC" />
              <path d="M 49,34 L 50,30 L 51.5,32 L 51,35 Z" fill="#DAA520" />
              
              {/* Far-Side Engine with Gold Trim */}
              <g>
                <rect x="28.5" y="34.5" width="10.5" height="3.4" rx="1.6" fill="#CBD5E1" />
                <ellipse cx="39" cy="36.2" rx="0.8" ry="1.6" fill="#1E293B" />
                <path d="M 29.5,34.5 C 28.5,35.5 28.5,36.8 29.5,37.8" fill="none" stroke="#DAA520" strokeWidth="0.5" />
              </g>
              
              {/* Tail Assembly with Gold Accents */}
              <g>
                <path d="M 15,39 L 11,27 C 10.5,25.5 11.5,24.5 13.5,24.5 L 18,25.5 L 23,40 Z" fill="#F0F4F8" />
                <path d="M 13,31 L 17,31.2 L 21,40 L 19,40 Z" fill="#DAA520" />
                <path d="M 2,30 L 16.5,23 L 22,19 L 19.5,21.5 L 14.5,24 L 2.5,31 Z" fill="#D9E2EC" />
                <path d="M 2,30 L 5,29 L 4,31 L 2.5,31 Z" fill="#DAA520" />
              </g>
              
              {/* Main Fuselage Body */}
              <defs>
                <linearGradient id="bodyGradPlane2" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#FFFFFF"/>
                  <stop offset="25%" stopColor="#F0F4F8"/>
                  <stop offset="60%" stopColor="#D9E2EC"/>
                  <stop offset="90%" stopColor="#BCCCDC"/>
                  <stop offset="100%" stopColor="#9FB3C8"/>
                </linearGradient>
              </defs>
              <path d="M 73.5,50 C 73.5,50 64,40 43,39 C 28.5,38 19,39 15,40 C 14.5,41 14.8,42.5 15.5,43 C 21.5,45 30,46.5 40.5,47.5 C 52.5,48.8 68,49.8 73.5,50 Z" fill="url(#bodyGradPlane2)" />
              
              {/* Nose Cone (Gold) */}
              <path d="M 69.5,47.5 C 71.5,48 73.5,50 73.5,50 C 72.5,47 70,44.5 68,43.5 Z" fill="#DAA520" />
              
              {/* Cockpit Windows */}
              <path d="M 66.5,43 C 63.5,42 58.5,42.2 57,44 C 56.5,45 58.5,45.5 64.5,45.5 Z" fill="#0F172A" />
              <path d="M 58,43.5 L 58.2,45 M 60.5,43 L 61,45 M 63.5,43.2 L 63.8,45" stroke="#64748B" strokeWidth="0.15" />
              
              {/* Passenger Windows */}
              <g fill="#1E293B">
                <rect x="37.8" y="41.5" width="1" height="1.4" rx="0.4"/>
                <rect x="39.5" y="41.6" width="1" height="1.4" rx="0.4"/>
                <rect x="41" y="41.7" width="1" height="1.4" rx="0.4"/>
                <rect x="42.8" y="41.8" width="1" height="1.4" rx="0.4"/>
                <rect x="44.5" y="41.9" width="1" height="1.4" rx="0.4"/>
              </g>
              
              {/* Door Frame */}
              <rect x="40.5" y="41" width="1.4" height="2.2" rx="0.3" fill="none" stroke="#D9E2EC" strokeWidth="0.15"/>
              
              {/* BOLD RED "JMI" LETTERING - sized to body height */}
              <g transform="translate(46, 45) skewX(-20) rotate(-3)">
                <text x="0" y="0" fontFamily="Impact, Arial Black, sans-serif" fontWeight="900" fontSize="5" fill="#E11D48" stroke="#9F1239" strokeWidth="0.12" letterSpacing="0.4">JMI</text>
              </g>
              
              {/* Near-Side Engine with Gold Accents */}
              <g>
                <path d="M 18.8,41 C 18.8,39.5 29.5,39 32,41 C 32.8,43.5 20,45 18.8,41 Z" fill="#CBD5E1" />
                <ellipse cx="30.8" cy="41.5" rx="1.1" ry="1.7" fill="#0F172A" />
                <ellipse cx="30.8" cy="41.5" rx="0.65" ry="1.1" fill="#475569" />
                <path d="M 30.8,39.8 C 31.5,40 31.5,43 30.8,43.2" fill="none" stroke="#DAA520" strokeWidth="0.4" />
              </g>
              
              {/* Near-Side Main Wing with Gold Accent */}
              <path d="M 30,45 L 8,50 C 5.5,50.5 3.5,51.5 3.2,53 C 3.5,54.5 6.5,55.5 9.5,54.5 L 41,49 Z" fill="#D9E2EC" />
              <path d="M 24,48.5 L 8,50 C 5.5,50.5 4.5,51.5 3.8,52.5 L 6.5,53.5 L 27,48.5 Z" fill="#DAA520" />
              <path d="M 3.2,53 L 2,49 C 1.5,47.5 2.8,47 4,48 L 8,50 Z" fill="#DAA520" />
              
              {/* Gold Stripe Along Lower Fuselage */}
              <path d="M 15.5,43 C 24,45 40,46.5 73.5,50" fill="none" stroke="#DAA520" strokeWidth="0.5" strokeLinecap="round" />
              
              {/* Navigation Lights */}
              <circle cx="51" cy="35" r="0.3" fill="#ef4444" opacity={isNight || isEvening ? "1" : "0.6"} filter={isNight || isEvening ? "url(#jmiGlow)" : undefined} />
              <circle cx="4" cy="48" r="0.3" fill="#10b981" opacity={isNight || isEvening ? "1" : "0.6"} filter={isNight || isEvening ? "url(#jmiGlow)" : undefined} />
            </g>
          )}

          {/* ========================================================================= */}
          {/* LAYER 1: Distant City Skyline & Communication Towers (Background)        */}
          {/* ========================================================================= */}
          {/* ========================================================================= */}
          {/* LAYER 0-B: DEEP BACKGROUND — ULTRA-DISTANT CITY SILHOUETTE              */}
          {/* ========================================================================= */}
          <g className="jmi-skyline-deep" fill={envTheme.bldgFar} opacity="0.45">
            {/* Ultra-far hazy city mass — just silhouettes, no windows */}
            <rect x="0"   y="148" width="38"  height="56" rx="1" />
            <rect x="42"  y="138" width="25"  height="66" rx="1" />
            <rect x="70"  y="152" width="20"  height="52" rx="1" />
            <rect x="95"  y="140" width="32"  height="64" rx="1" />
            <rect x="132" y="130" width="18"  height="74" rx="1" />
            <rect x="154" y="145" width="28"  height="59" rx="1" />
            {/* Distant spire left */}
            <polygon points="143,130 146,105 149,130" />
            <rect x="190" y="143" width="40"  height="61" rx="1" />
            <rect x="236" y="133" width="22"  height="71" rx="1" />
            <rect x="262" y="150" width="30"  height="54" rx="1" />
            <rect x="296" y="137" width="18"  height="67" rx="1" />
            {/* Pentagon/pointed far bldg */}
            <polygon points="318,138 328,122 338,138" />
            <rect x="318" y="138" width="20"  height="66" rx="1" />
            <rect x="345" y="144" width="35"  height="60" rx="1" />
            <rect x="385" y="128" width="26"  height="76" rx="1" />
            <rect x="416" y="148" width="20"  height="56" rx="1" />
            <rect x="440" y="136" width="42"  height="68" rx="1" />
            <rect x="487" y="150" width="22"  height="54" rx="1" />
            <rect x="514" y="140" width="28"  height="64" rx="1" />
            {/* Tall distant landmark with stepped crown */}
            <rect x="548" y="118" width="18"  height="86" rx="1" />
            <rect x="544" y="128" width="26"  height="10" rx="1" />
            <rect x="540" y="136" width="34"  height="6"  rx="1" />
            <rect x="575" y="142" width="30"  height="62" rx="1" />
            <rect x="610" y="132" width="24"  height="72" rx="1" />
            <rect x="638" y="148" width="19"  height="56" rx="1" />
            {/* Distant dome building */}
            <ellipse cx="677" cy="140" rx="18" ry="7" />
            <rect x="659" y="140" width="36"  height="64" rx="1" />
            <rect x="705" y="136" width="28"  height="68" rx="1" />
            <rect x="738" y="144" width="22"  height="60" rx="1" />
            <rect x="766" y="130" width="30"  height="74" rx="1" />
            {/* Distant needle tower */}
            <line x1="801" y1="204" x2="801" y2="100" stroke={envTheme.bldgFar} strokeWidth="3" />
            <polygon points="799,100 801,82 803,100" />
            <rect x="795" y="130" width="12"  height="74" rx="1" />
            <rect x="812" y="145" width="26"  height="59" rx="1" />
            <rect x="843" y="133" width="20"  height="71" rx="1" />
            <rect x="868" y="148" width="32"  height="56" rx="1" />
            <rect x="905" y="138" width="22"  height="66" rx="1" />
            <rect x="932" y="124" width="35"  height="80" rx="1" />
            {/* Two-stepped far tower */}
            <rect x="972" y="132" width="14"  height="72" rx="1" />
            <rect x="968" y="142" width="22"  height="10" rx="1" />
            <rect x="990" y="144" width="28"  height="60" rx="1" />
            <rect x="1023" y="136" width="20" height="68" rx="1" />
            <rect x="1048" y="148" width="30" height="56" rx="1" />
            {/* Far arch building */}
            <rect x="1082" y="140" width="36" height="64" rx="1" />
            <ellipse cx="1100" cy="140" rx="18" ry="9" />
            <rect x="1125" y="128" width="22" height="76" rx="1" />
            <rect x="1152" y="145" width="28" height="59" rx="1" />
            <rect x="1185" y="133" width="15" height="71" rx="1" />
          </g>

          {/* ========================================================================= */}
          {/* LAYER 1: DESIGNER LANDMARK BUILDINGS — MID BACKGROUND                   */}
          {/* ========================================================================= */}
          <g className="jmi-skyline-far" opacity="0.88">
            <defs>
              <linearGradient id="bldgEdge" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#000000" stopOpacity="0.28" />
              </linearGradient>
              <linearGradient id="glassFacadeBlue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e3a6e" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#0a1628" stopOpacity="0.95" />
              </linearGradient>
              <linearGradient id="copperFacade" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7c3a1e" stopOpacity="0.85" />
                <stop offset="100%" stopColor="#3d1a08" stopOpacity="0.95" />
              </linearGradient>
            </defs>

            {/* ── 1. BURJ-STYLE TAPERING TOWER (left zone) ── */}
            <g fill={envTheme.bldgFar}>
              {/* Base plinth */}
              <rect x="18" y="165" width="52" height="39" />
              <rect x="18" y="165" width="52" height="39" fill="url(#bldgEdge)" opacity="0.5" />
              {/* Mid body */}
              <rect x="24" y="135" width="40" height="30" />
              {/* Narrow upper shaft */}
              <rect x="33" y="100" width="22" height="35" />
              {/* Spire */}
              <polygon points="33,100 44,72 55,100" fill={envTheme.bldgMid} opacity="0.9" />
              <line x1="44" y1="72" x2="44" y2="58" stroke="#94a3b8" strokeWidth="1.2" />
              <circle cx="44" cy="57" r="1.6" fill="#ef4444" className="jmi-tower-beacon" />
              {/* Step ledges */}
              <rect x="16" y="165" width="56" height="2" fill={envTheme.bldgMid} opacity="0.5" />
              <rect x="22" y="135" width="44" height="2" fill={envTheme.bldgMid} opacity="0.4" />
              <rect x="31" y="100" width="26" height="2" fill={envTheme.bldgMid} opacity="0.4" />
              {/* Windows */}
              {renderWindowGrid(28, 140, 3, 2, 11, 10, 7, 5)}
              {renderWindowGrid(35, 107, 2, 4, 10, 9, 6, 5)}
              {renderWindowGrid(21, 170, 4, 2, 11, 10, 7, 5)}
              {/* Decorative fin details */}
              <line x1="18"  y1="165" x2="18"  y2="204" stroke={envTheme.bldgMid} strokeWidth="0.7" opacity="0.5" />
              <line x1="70"  y1="165" x2="70"  y2="204" stroke={envTheme.bldgMid} strokeWidth="0.7" opacity="0.5" />
            </g>

            {/* ── 2. CRYSTAL SHARD / SHARD LONDON STYLE (mid-left) ── */}
            <g>
              <polygon points="90,204 96,118 108,118 114,204" fill={envTheme.bldgFar} />
              <polygon points="96,118 102,80 108,118" fill={envTheme.bldgMid} opacity="0.85" />
              {/* Glass facade stripes */}
              <line x1="96" y1="118" x2="90"  y2="204" stroke={envTheme.bldgMid} strokeWidth="0.6" opacity="0.5" />
              <line x1="100" y1="118" x2="94" y2="204" stroke={envTheme.bldgMid} strokeWidth="0.5" opacity="0.4" />
              <line x1="104" y1="118" x2="98" y2="204" stroke={envTheme.bldgMid} strokeWidth="0.5" opacity="0.4" />
              <line x1="108" y1="118" x2="102" y2="204" stroke={envTheme.bldgMid} strokeWidth="0.5" opacity="0.4" />
              {/* Glint on shard tip */}
              <circle cx="102" cy="80" r="1.2" fill="#e0f2fe" opacity="0.8" />
              {/* Windows on shard */}
              {renderWindowGrid(94, 132, 1, 6, 8, 12, 5, 5)}
            </g>

            {/* ── 3. ART DECO CHRYSLER-STYLE (mid-left) ── */}
            <g fill={envTheme.bldgFar}>
              <rect x="135" y="150" width="50" height="54" />
              <rect x="140" y="130" width="40" height="20" />
              <rect x="148" y="112" width="24" height="18" />
              {/* Deco crown — stacked arches */}
              <polygon points="148,112 160,88 172,112" fill={envTheme.bldgMid} opacity="0.9" />
              <polygon points="151,105 160,90 169,105" fill={envTheme.bldgFar} />
              <polygon points="154,100 160,92 166,100" fill={envTheme.bldgMid} opacity="0.7" />
              <line x1="160" y1="88" x2="160" y2="74" stroke="#94a3b8" strokeWidth="1" />
              <circle cx="160" cy="73" r="1.4" fill="#fbbf24" className="jmi-diamond-spire" />
              {/* Deco horizontal bands */}
              {[150, 163, 175, 188].map(y => (
                <rect key={y} x="133" y={y} width="54" height="1" fill={envTheme.bldgMid} opacity="0.4" />
              ))}
              {/* Eagle-wing Art Deco buttresses */}
              <path d="M 135,155 Q 120,150 118,158 Q 120,162 135,158" fill={envTheme.bldgMid} opacity="0.6" />
              <path d="M 185,155 Q 200,150 202,158 Q 200,162 185,158" fill={envTheme.bldgMid} opacity="0.6" />
              {renderWindowGrid(143, 136, 3, 1, 12, 10, 7, 5)}
              {renderWindowGrid(150, 155, 3, 3, 12, 13, 7, 5)}
              {renderWindowGrid(151, 116, 2, 2, 11, 9, 6, 5)}
            </g>

            {/* ── 4. GLASS CURTAIN-WALL TOWER (centre-left) ── */}
            <g>
              <rect x="218" y="108" width="62" height="96" fill="url(#glassFacadeBlue)" />
              <rect x="218" y="108" width="62" height="96" fill="url(#bldgEdge)" opacity="0.5" />
              {/* Setback top floor */}
              <rect x="226" y="96" width="46" height="12" fill={envTheme.bldgFar} />
              {/* Glass facade vertical mullions */}
              {[224,232,240,248,256,264,272].map(x => (
                <line key={x} x1={x} y1="108" x2={x} y2="204" stroke="#1e40af" strokeWidth="0.6" opacity="0.35" />
              ))}
              {/* Glass facade horizontal spandrels */}
              {[120,132,144,156,168,180,192].map(y => (
                <rect key={y} x="218" y={y} width="62" height="1" fill="#1e40af" opacity="0.25" />
              ))}
              {renderWindowGrid(222, 112, 5, 7, 11, 12, 6.5, 5)}
              {renderWindowGrid(230, 100, 4, 1, 10, 8, 6, 4)}
              {/* Crown detail */}
              <rect x="216" y="106" width="66" height="3" fill={envTheme.bldgMid} opacity="0.7" />
              <line x1="249" y1="96" x2="249" y2="80" stroke="#94a3b8" strokeWidth="1.2" />
              <circle cx="249" cy="79" r="1.5" fill="#38bdf8" className="jmi-tower-beacon" style={{ animationDelay: "0.7s" }} />
            </g>

            {/* ── 5. STEPPED PYRAMID TOWER (centre) ── */}
            <g fill={envTheme.bldgFar}>
              <rect x="308" y="166" width="58" height="38" />
              <rect x="314" y="148" width="46" height="18" />
              <rect x="322" y="130" width="30" height="18" />
              <rect x="328" y="112" width="18" height="18" />
              <polygon points="328,112 337,94 346,112" fill={envTheme.bldgMid} opacity="0.85" />
              <line x1="337" y1="94" x2="337" y2="78" stroke="#94a3b8" strokeWidth="1.2" />
              <circle cx="337" cy="77" r="1.6" fill="#ef4444" className="jmi-tower-beacon" style={{ animationDelay: "1.1s" }} />
              {/* Step ledges */}
              {[[306,166,62],[312,148,50],[320,130,34],[326,112,22]].map(([x,y,w],i) => (
                <rect key={i} x={x} y={y} width={w} height="2" fill={envTheme.bldgMid} opacity="0.5" />
              ))}
              {renderWindowGrid(312, 155, 4, 1, 11, 10, 7, 5)}
              {renderWindowGrid(318, 137, 2, 1, 11, 10, 6, 5)}
              {renderWindowGrid(324, 118, 1, 1, 10, 10, 6, 5)}
              {renderWindowGrid(311, 170, 4, 2, 12, 11, 7, 5)}
            </g>

            {/* ── 6. CYLINDRICAL TOWER w/ RIBBED FACADE ── */}
            <g>
              <ellipse cx="430" cy="204" rx="30" ry="5" fill={envTheme.bldgFar} />
              <rect x="400" y="118" width="60" height="86" fill={envTheme.bldgFar} />
              <ellipse cx="430" cy="118" rx="30" ry="8" fill={envTheme.bldgMid} opacity="0.85" />
              {/* Ribbed facade rings */}
              {[130,145,160,175,192].map(y => (
                <ellipse key={y} cx="430" cy={y} rx="30" ry="2.5" fill="none" stroke={envTheme.bldgMid} strokeWidth="0.8" opacity="0.4" />
              ))}
              {/* Vertical ribs */}
              {[405,413,421,429,437,445,453].map(x => (
                <line key={x} x1={x} y1="118" x2={x} y2="204" stroke={envTheme.bldgMid} strokeWidth="0.5" opacity="0.35" />
              ))}
              {renderWindowGrid(408, 126, 4, 6, 12, 12, 6, 5)}
              {/* Top disc detail */}
              <ellipse cx="430" cy="108" rx="22" ry="5" fill={envTheme.bldgMid} opacity="0.7" />
              <line x1="430" y1="103" x2="430" y2="85" stroke="#94a3b8" strokeWidth="1.4" />
              <circle cx="430" cy="84" r="1.6" fill="#10b981" className="jmi-tower-beacon" style={{ animationDelay: "0.4s" }} />
            </g>

            {/* ── 7. TWISTED / ROTATING TOWER (centre-right, signature silhouette) ── */}
            <g>
              {/* Base wide, top rotated — approximated with trapezoids */}
              <polygon points="508,204 510,165 538,165 540,204" fill={envTheme.bldgFar} />
              <polygon points="510,165 513,130 535,130 538,165" fill={envTheme.bldgFar} />
              <polygon points="513,130 517,100 533,100 535,130" fill={envTheme.bldgFar} />
              <polygon points="517,100 521,72 529,72 533,100"   fill={envTheme.bldgFar} />
              {/* Twist lines */}
              {[[508,204,513,130],[510,165,517,100],[513,130,521,72]].map(([x1,y1,x2,y2],i) => (
                <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={envTheme.bldgMid} strokeWidth="0.5" opacity="0.45" />
              ))}
              <line x1="525" y1="72" x2="525" y2="56" stroke="#94a3b8" strokeWidth="1.2" />
              <circle cx="525" cy="55" r="1.5" fill="#f59e0b" className="jmi-tower-beacon" style={{ animationDelay: "1.4s" }} />
              {renderWindowGrid(512, 108, 2, 5, 9, 11, 5, 4)}
              {renderWindowGrid(512, 138, 2, 3, 10, 11, 6, 5)}
              {renderWindowGrid(512, 172, 3, 2, 11, 11, 7, 5)}
            </g>

            {/* ── 8. COPPER-CLAD HISTORIC MANSION STYLE (right zone) ── */}
            <g>
              <rect x="598" y="148" width="80" height="56" fill="url(#copperFacade)" />
              <rect x="598" y="148" width="80" height="56" fill="url(#bldgEdge)" opacity="0.4" />
              {/* Mansard roofline */}
              <polygon points="598,148 605,128 673,128 678,148" fill={envTheme.bldgFar} />
              {/* Dormer windows on roof */}
              {[616,634,652].map(x => (
                <g key={x}>
                  <rect x={x} y="130" width="8" height="9" fill={envTheme.bldgMid} opacity="0.7" />
                  <polygon points={`${x},130 ${x+4},124 ${x+8},130`} fill={envTheme.bldgMid} opacity="0.6" />
                </g>
              ))}
              {/* Classical columns */}
              {[604,618,634,650,664].map(x => (
                <line key={x} x1={x} y1="148" x2={x} y2="204" stroke={envTheme.bldgMid} strokeWidth="2" opacity="0.4" />
              ))}
              {/* Cornice line */}
              <rect x="596" y="148" width="84" height="2.5" fill={envTheme.bldgMid} opacity="0.6" />
              <rect x="596" y="170" width="84" height="1.5" fill={envTheme.bldgMid} opacity="0.4" />
              {/* Grand arched entrance */}
              <rect x="627" y="178" width="22" height="26" fill={envTheme.bldgFar} rx="11" />
              <ellipse cx="638" cy="178" rx="11" ry="6" fill={envTheme.bldgFar} />
              {renderWindowGrid(603, 155, 5, 1, 13, 10, 8, 7)}
              {renderWindowGrid(603, 173, 4, 1, 15, 10, 8, 6)}
            </g>

            {/* ── 9. FUTURISTIC DIAMOND / RHOMBUS TOWER (far right) ── */}
            <g fill={envTheme.bldgFar}>
              <polygon points="720,204 722,160 746,130 770,160 772,204" />
              <polygon points="722,160 746,130 770,160 746,100" fill={envTheme.bldgMid} opacity="0.7" />
              {/* Facet lines */}
              <line x1="746" y1="100" x2="722" y2="160" stroke={envTheme.bldgMid} strokeWidth="0.6" opacity="0.5" />
              <line x1="746" y1="100" x2="770" y2="160" stroke={envTheme.bldgMid} strokeWidth="0.6" opacity="0.5" />
              <line x1="746" y1="130" x2="722" y2="180" stroke={envTheme.bldgMid} strokeWidth="0.5" opacity="0.4" />
              <line x1="746" y1="130" x2="770" y2="180" stroke={envTheme.bldgMid} strokeWidth="0.5" opacity="0.4" />
              {/* Diamond tip glow */}
              <circle cx="746" cy="99" r="2" fill="#e0f2fe" opacity="0.7" />
              <line x1="746" y1="97" x2="746" y2="82" stroke="#94a3b8" strokeWidth="1.2" />
              <circle cx="746" cy="81" r="1.5" fill="#38bdf8" className="jmi-tower-beacon" style={{ animationDelay: "0.9s" }} />
              {renderWindowGrid(732, 145, 2, 4, 10, 14, 6, 6)}
            </g>

            {/* ── 10. PAGODA-STYLE TIERED TOWER (far right) ── */}
            <g fill={envTheme.bldgFar}>
              {/* Tiers from bottom up */}
              <rect x="820" y="178" width="52" height="26" />
              <rect x="826" y="162" width="40" height="16" />
              <rect x="832" y="146" width="28" height="16" />
              <rect x="838" y="132" width="16" height="14" />
              {/* Pagoda upturned eave tips */}
              <path d="M 820,178 Q 808,174 806,180 Q 808,184 820,182" fill={envTheme.bldgMid} opacity="0.6" />
              <path d="M 872,178 Q 884,174 886,180 Q 884,184 872,182" fill={envTheme.bldgMid} opacity="0.6" />
              <path d="M 826,162 Q 816,158 814,164 Q 816,168 826,166" fill={envTheme.bldgMid} opacity="0.5" />
              <path d="M 866,162 Q 876,158 878,164 Q 876,168 866,166" fill={envTheme.bldgMid} opacity="0.5" />
              {/* Tier ledges */}
              {[[818,178,56],[824,162,44],[830,146,32],[836,132,20]].map(([x,y,w],i) => (
                <rect key={i} x={x} y={y} width={w} height="2" fill={envTheme.bldgMid} opacity="0.5" />
              ))}
              {/* Central finial */}
              <line x1="846" y1="132" x2="846" y2="110" stroke="#94a3b8" strokeWidth="2" />
              <polygon points="843,110 846,95 849,110" fill={envTheme.bldgMid} opacity="0.85" />
              <circle cx="846" cy="94" r="1.5" fill="#fbbf24" className="jmi-diamond-spire" style={{ animationDelay: "1.8s" }} />
              {renderWindowGrid(826, 167, 3, 1, 12, 10, 7, 5)}
              {renderWindowGrid(823, 182, 4, 2, 11, 10, 7, 5)}
            </g>

            {/* ── 11. STACKED BOXES MODERN (far right cluster) ── */}
            <g fill={envTheme.bldgFar}>
              <rect x="910" y="160" width="45" height="44" />
              <rect x="916" y="142" width="33" height="18" />
              <rect x="905" y="134" width="20" height="8" />
              <rect x="940" y="128" width="18" height="14" />
              {/* Box ledges */}
              <rect x="908"  y="142" width="49" height="2" fill={envTheme.bldgMid} opacity="0.5" />
              <rect x="908"  y="160" width="49" height="2" fill={envTheme.bldgMid} opacity="0.5" />
              {/* Vertical seams */}
              <line x1="925" y1="134" x2="925" y2="204" stroke={envTheme.bldgMid} strokeWidth="0.7" opacity="0.4" />
              <line x1="940" y1="142" x2="940" y2="204" stroke={envTheme.bldgMid} strokeWidth="0.7" opacity="0.4" />
              {renderWindowGrid(914, 148, 2, 1, 12, 10, 7, 5)}
              {renderWindowGrid(913, 165, 3, 2, 13, 12, 8, 5)}
              <line x1="949" y1="128" x2="949" y2="106" stroke="#94a3b8" strokeWidth="1.2" />
              <circle cx="949" cy="105" r="1.4" fill="#ef4444" className="jmi-tower-beacon" style={{ animationDelay: "2.1s" }} />
            </g>

            {/* ── 12. NEEDLE SPIRE (rightmost) ── */}
            <g>
              <rect x="1000" y="150" width="30" height="54" fill={envTheme.bldgFar} />
              <rect x="1004" y="132" width="22" height="18" fill={envTheme.bldgFar} />
              <polygon points="1004,132 1015,100 1026,132" fill={envTheme.bldgMid} opacity="0.8" />
              <line x1="1015" y1="100" x2="1015" y2="80" stroke="#94a3b8" strokeWidth="1.4" />
              <circle cx="1015" cy="79" r="1.6" fill="#f59e0b" className="jmi-tower-beacon" style={{ animationDelay: "0.6s" }} />
              {/* Horizontal crown rings */}
              <ellipse cx="1015" cy="100" rx="11" ry="3" fill={envTheme.bldgMid} opacity="0.6" />
              {renderWindowGrid(1004, 138, 2, 1, 11, 10, 7, 5)}
              {renderWindowGrid(1002, 155, 2, 3, 13, 12, 8, 5)}
              <rect x="998" y="148" width="34" height="2" fill={envTheme.bldgMid} opacity="0.5" />
            </g>
          </g>

          {/* ========================================================================= */}
          {/* LAYER 2: MASTER CITY LAYOUT (MALL ON LEFT, BUS STOP IN MIDDLE, CONNECTED JMI TOWER & WIDE BUILDING ON FAR RIGHT) */}
          {/* ========================================================================= */}
          <g className="jmi-master-cityscape">

            {/* --- 1. LEFT STRUCTURE: BUYER LUXURY MALL (TALL & WIDE) --- */}
            {/* Wider building: x=30 to 280, width=250 (was 210) */}
            <g className="jmi-buyer-mall">
              {/* Main building - FULL TOWER HEIGHT & WIDER */}
              <rect
                x="30"
                y="94"
                width="250"
                height="102"
                fill="url(#jmiMallFacade)"
                stroke="#334155"
                strokeWidth="0.8"
                rx="2"
              />
              
              {/* Architectural roof crown */}
              <rect x="25" y="90" width="260" height="4" fill="#1e293b" rx="1" />

              {/* SIGNBOARD AT TOP FLOOR - Covers top section only */}
              <g className="jmi-mall-marquee">
                {/* Top floor signboard */}
                <rect
                  x="32"
                  y="96"
                  width="246"
                  height="42"
                  fill="linear-gradient(135deg, #0b132b 0%, #1e293b 50%, #0f172a 100%)"
                  rx="2"
                  stroke="#fbbf24"
                  strokeWidth="2.5"
                  filter={showBuildingLights ? "url(#jmiGlow)" : undefined}
                />

                {/* Decorative border accents */}
                <rect
                  x="34"
                  y="98"
                  width="242"
                  height="38"
                  fill="none"
                  stroke="#d97706"
                  strokeWidth="0.8"
                  rx="1.5"
                  opacity="0.4"
                />

                {/* FLEX-STYLE LAYOUT: Logo and Name in same horizontal line */}
                {buyerLogo ? (
                  <>
                    {/* Logo on left side */}
                    <image
                      href={buyerLogo}
                      x="45"
                      y="102"
                      width="38"
                      height="32"
                      preserveAspectRatio="xMidYMid meet"
                    />
                    {/* Name/Initials next to logo - dynamically sized */}
                    <text
                      x="92"
                      y="124"
                      fontSize={displayName.length <= 3 ? "26" : displayName.length <= 5 ? "22" : displayName.length <= 8 ? "18" : "14"}
                      fontWeight="900"
                      fontFamily="system-ui, -apple-system, sans-serif"
                      fill="#fbbf24"
                      textAnchor="start"
                      letterSpacing={displayName.length <= 5 ? "4" : displayName.length <= 8 ? "3" : "2"}
                      filter={showBuildingLights ? "url(#jmiGlow)" : undefined}
                    >
                      {displayName}
                    </text>
                  </>
                ) : (
                  <>
                    {/* Star icon on left */}
                    <circle cx="64" cy="117" r="16" fill="#f59e0b" />
                    <text
                      x="64"
                      y="126"
                      fontSize="18"
                      fontWeight="900"
                      fill="#0f172a"
                      textAnchor="middle"
                    >
                      ★
                    </text>
                    {/* Name/Initials next to star */}
                    <text
                      x="92"
                      y="124"
                      fontSize={displayName.length <= 3 ? "26" : displayName.length <= 5 ? "22" : displayName.length <= 8 ? "18" : "14"}
                      fontWeight="900"
                      fontFamily="system-ui, -apple-system, sans-serif"
                      fill="#fbbf24"
                      textAnchor="start"
                      letterSpacing={displayName.length <= 5 ? "4" : displayName.length <= 8 ? "3" : "2"}
                      filter={showBuildingLights ? "url(#jmiGlow)" : undefined}
                    >
                      {displayName}
                    </text>
                  </>
                )}
                
                {/* Decorative sparkles */}
                <circle cx="265" cy="105" r="2" fill="#fbbf24" opacity="0.8">
                  <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite" />
                </circle>
                <circle cx="42" cy="108" r="1.8" fill="#93c5fd" opacity="0.8">
                  <animate attributeName="opacity" values="0.3;1;0.3" dur="2.5s" repeatCount="indefinite" />
                </circle>
              </g>

              {/* Building windows below signboard */}
              {renderWindowGrid(38, 145, 15, 2, 15, 10, 8, 5)}

              {/* Animated Entrance Door at ground level */}
              <g className="jmi-mall-entrance">
                {/* Door frame */}
                <rect x="145" y="175" width="20" height="21" fill="#1e293b" stroke="#fbbf24" strokeWidth="1" rx="1" />
                
                {/* Left door panel - animated */}
                <rect 
                  x="145" 
                  y="175" 
                  width="10" 
                  height="21" 
                  fill="url(#doorGradient)" 
                  stroke="#0b132b" 
                  strokeWidth="0.6" 
                  rx="0.5"
                  className="jmi-door-left"
                />
                
                {/* Right door panel - animated */}
                <rect 
                  x="155" 
                  y="175" 
                  width="10" 
                  height="21" 
                  fill="url(#doorGradient)" 
                  stroke="#0b132b" 
                  strokeWidth="0.6" 
                  rx="0.5"
                  className="jmi-door-right"
                />
                
                {/* Door handles */}
                <circle cx="153" cy="185.5" r="1" fill="#fbbf24" className="jmi-door-left" />
                <circle cx="157" cy="185.5" r="1" fill="#fbbf24" className="jmi-door-right" />
                
                {/* Entrance arch above door */}
                <path d="M 143,175 Q 155,168 167,175" stroke="#fbbf24" strokeWidth="1.2" fill="none" opacity="0.9" />
              </g>
            </g>

            {/* --- ✨ MIDDLE OPEN AVENUE: BUS STOP WITH SWITCHING DIGITAL BILLBOARD (x=380 to 500) --- */}
            <g className="jmi-bus-stop-zone">
              {/* Modern Bus Stop Passenger Shelter */}
              <rect x="380" y="172" width="105" height="24" fill="rgba(15, 23, 42, 0.75)" rx="2" stroke="#64748b" strokeWidth="0.8" />
              <line x1="380" y1="172" x2="485" y2="172" stroke="#38bdf8" strokeWidth="2" />
              <rect x="383" y="174" width="99" height="18" fill="rgba(56, 189, 248, 0.18)" />
              <rect x="395" y="186" width="45" height="3" fill="#94a3b8" />
              <line x1="400" y1="189" x2="400" y2="196" stroke="#475569" strokeWidth="1.2" />
              <line x1="435" y1="189" x2="435" y2="196" stroke="#475569" strokeWidth="1.2" />

              <circle cx="410" cy="181" r="1.8" fill="#fde047" />
              <rect x="409" y="183" width="2" height="4.5" fill="#3b82f6" />
              <circle cx="426" cy="181" r="1.8" fill="#e2e8f0" />
              <rect x="425" y="183" width="2" height="4.5" fill="#e11d48" />

              {/* Digital Hoarding Billboard Tower Columns */}
              <line x1="410" y1="172" x2="410" y2="116" stroke="#64748b" strokeWidth="2.5" />
              <line x1="450" y1="172" x2="450" y2="116" stroke="#64748b" strokeWidth="2.5" />

              {/* Billboard Outer Display Case */}
              <rect
                x="380"
                y="108"
                width="100"
                height="34"
                fill="#030712"
                rx="2"
                stroke="#38bdf8"
                strokeWidth="1.2"
                filter={showBuildingLights ? "url(#jmiGlow)" : undefined}
              />

              <g className="jmi-billboard-frame-a">
                <rect x="383" y="111" width="94" height="28" fill="#0f172a" rx="1" />
                {buyerLogo ? (
                  <>
                    <image
                      href={buyerLogo}
                      x="387"
                      y="115"
                      width="24"
                      height="20"
                      preserveAspectRatio="xMidYMid meet"
                    />
                    <text x="437" y="122" fontSize="5.5" fontWeight="900" fill="#fde047" textAnchor="middle">
                      {buyerName.slice(0, 10).toUpperCase()}
                    </text>
                    <text x="437" y="130" fontSize="3.5" fontWeight="700" fill="#93c5fd" textAnchor="middle">
                      PREMIER PARTNER
                    </text>
                  </>
                ) : (
                  <>
                    <circle cx="400" cy="125" r="6.5" fill="#f59e0b" />
                    <text x="400" y="128" fontSize="6" fontWeight="900" fill="#0f172a" textAnchor="middle">
                      ★
                    </text>
                    <text x="437" y="122" fontSize="5.5" fontWeight="900" fill="#fde047" textAnchor="middle">
                      {buyerName.slice(0, 10).toUpperCase()}
                    </text>
                    <text x="437" y="130" fontSize="3.5" fontWeight="700" fill="#93c5fd" textAnchor="middle">
                      PREMIER PARTNER
                    </text>
                  </>
                )}
              </g>

              <g className="jmi-billboard-frame-b">
                <rect x="383" y="111" width="94" height="28" fill="#ffffff" rx="1" />
                <image
                  href={jmiLogoImg}
                  x="395"
                  y="114"
                  width="70"
                  height="22"
                  preserveAspectRatio="xMidYMid meet"
                />
              </g>
            </g>

            {/* --- 2. FAR RIGHT CONNECTED COMPLEX: WIDE BUILDING & JMI TOWER (x=680 to 1160) --- */}
            <g className="jmi-connected-right-complex">

              {/* --- 2B. JMI TOWER (x=680 to 860, width=180) NOW ON LEFT --- */}
              <g className="jmi-hq-landmark">
                <rect
                  x="680"
                  y="94"
                  width="180"
                  height="102"
                  fill="url(#jmiGlassFacade)"
                  stroke="#38bdf8"
                  strokeWidth="0.8"
                  rx="2"
                />

                {/* Tower Architectural Roof Crown & Spire */}
                <polygon
                  points="680,94 770,78 860,94"
                  fill="#0f172a"
                  stroke="#d97706"
                  strokeWidth="0.8"
                />
                <line x1="770" y1="78" x2="770" y2="65" stroke="#f59e0b" strokeWidth="1.6" />
                <circle
                  cx="770"
                  cy="65"
                  r="2.2"
                  fill="#fbbf24"
                  filter={showBuildingLights ? "url(#jmiGlow)" : undefined}
                  className="jmi-diamond-spire"
                />

                {renderWindowGrid(692, 98, 14, 2, 11, 9, 6.5, 5)}

                {/* Direct Crisp Official JMI Logo Image on Tower */}
                <g className="jmi-tower-direct-logo">
                  <rect
                    x="705"
                    y="118"
                    width="130"
                    height="34"
                    fill="#ffffff"
                    rx="3"
                    stroke="#f59e0b"
                    strokeWidth="1.4"
                    filter={showBuildingLights ? "url(#jmiGlow)" : undefined}
                  />
                  <image
                    href={jmiLogoImg}
                    x="715"
                    y="120"
                    width="110"
                    height="30"
                    preserveAspectRatio="xMidYMid meet"
                  />
                </g>

                {renderWindowGrid(692, 156, 14, 2, 11, 9, 6.5, 5)}

                <rect x="740" y="176" width="60" height="20" fill="#020617" stroke="#f59e0b" strokeWidth="0.8" rx="1" />
                <rect x="750" y="181" width="40" height="15" fill="rgba(253, 224, 71, 0.4)" rx="1" />
                <line x1="770" y1="181" x2="770" y2="196" stroke="#020617" strokeWidth="1" />
              </g>

              {/* --- SEAMLESS JOINT: thin connector strip between tower and wide building --- */}
              <g className="jmi-tower-joint">
                <rect x="860" y="146" width="10" height="50" fill="#0f172a" stroke="#38bdf8" strokeWidth="0.8" />
                <line x1="860" y1="171" x2="870" y2="171" stroke="#fbbf24" strokeWidth="1" />
              </g>

              {/* --- 2A. WIDE BUILDING: half the tower height (tower 102px → wide 50px), starts y=146 → y=196. Now on RIGHT (x=870 to 1170) --- */}
              <g className="jmi-right-building">
                <rect
                  x="870"
                  y="146"
                  width="300"
                  height="50"
                  fill={envTheme.bldgMid}
                  stroke="#334155"
                  strokeWidth="0.8"
                  rx="2"
                />

                {/* PURE GLOWING "JMI" TEXT — ON TOP OF BUILDING (NO ROOF TRIANGLE) */}
                <g className="jmi-pure-glowing-jmi">
                  <text
                    x="1020"
                    y="140"
                    fontSize="16"
                    fontWeight="900"
                    fontFamily="system-ui, -apple-system, sans-serif"
                    fill="url(#jmiRedNeonGrad)"
                    textAnchor="middle"
                    letterSpacing="5"
                    className="jmi-pure-neon-text"
                  >
                    JMI
                  </text>
                </g>

                {/* Windows Matrix (2 rows to match compact height) */}
                {renderWindowGrid(882, 152, 20, 2, 14, 11, 8, 6)}
                
                {/* Wide Entrance Door — centered on building */}
                <defs>
                  <linearGradient id="wideDoorGradient" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#ec4899" />
                    <stop offset="25%" stopColor="#f97316" />
                    <stop offset="50%" stopColor="#eab308" />
                    <stop offset="75%" stopColor="#06b6d4" />
                    <stop offset="100%" stopColor="#8b5cf6" />
                  </linearGradient>
                </defs>
                
                {/* Wide door frame (larger than regular door) */}
                <rect
                  x="950"
                  y="170"
                  width="140"
                  height="26"
                  fill="url(#wideDoorGradient)"
                  stroke="#0b132b"
                  strokeWidth="1"
                  rx="2"
                />
                
                {/* Door panels separator */}
                <line x1="1020" y1="170" x2="1020" y2="196" stroke="#0b132b" strokeWidth="0.8" opacity="0.6" />
                
                {/* Door handles - left and right */}
                <circle cx="980" cy="183" r="1.5" fill="#fbbf24" opacity="0.9" />
                <circle cx="1060" cy="183" r="1.5" fill="#fbbf24" opacity="0.9" />
                
                {/* Door shine effect */}
                <rect x="951" y="171" width="68" height="12" fill="#ffffff" opacity="0.25" rx="1" />
                <rect x="1021" y="171" width="68" height="12" fill="#ffffff" opacity="0.25" rx="1" />
                
                {/* Door arch/crown */}
                <path d="M 945,170 Q 1020,155 1095,170" stroke="#fbbf24" strokeWidth="1.2" fill="none" opacity="0.9" />
              </g>
            </g>
          </g>

          {/* ========================================================================= */}
          {/* LAYER 3: Street Infrastructure, Trees & Lighting in Open Spaces           */}
          {/* ========================================================================= */}
          <g className="jmi-street-infrastructure">
            <rect x="0" y="196" width="1200" height="8" fill="#334155" />
            <line x1="0" y1="196" x2="1200" y2="196" stroke="#475569" strokeWidth="1" />
            <line x1="0" y1="204" x2="1200" y2="204" stroke="#1e293b" strokeWidth="1.5" />

            {/* Urban Trees in Open Gaps & Plazas */}
            {[25, 290, 360, 510, 600, 660, 1180].map((tx, idx) => (
              <g key={`tree-${idx}`}>
                <rect x={tx - 5} y="194" width="10" height="5" fill="#1e293b" rx="0.5" />
                <line x1={tx} y1="194" x2={tx} y2="183" stroke="#78350f" strokeWidth="2" />
                <circle
                  cx={tx}
                  cy="178"
                  r="8"
                  fill={isNight ? "#064e3b" : "#10b981"}
                  opacity="0.9"
                />
                <circle
                  cx={tx - 3}
                  cy="181"
                  r="5"
                  fill={isNight ? "#022c22" : "#059669"}
                  opacity="0.85"
                />
              </g>
            ))}

            {/* Street Lights System — Enhanced Detail */}
            {[35, 305, 530, 650, 1175].map((lx, idx) => (
              <g key={`lamp-${idx}`}>
                {/* Light cone beam when ON — proper downward cone FROM BULB */}
                {showStreetLights && envTheme.lightOpacity > 0 && (
                  <>
                    {/* Main cone beam FROM BULB at y=155 */}
                    <polygon
                      points={`${lx + 9},155 ${lx + 15},155 ${lx + 40},240 ${lx - 16},240`}
                      fill="url(#jmiStreetLampBeam)"
                      opacity={envTheme.lightOpacity * (isRain ? 0.9 : 0.65)}
                    />
                    {/* Bright core beam center FROM BULB */}
                    <polygon
                      points={`${lx + 10},155 ${lx + 14},155 ${lx + 24},240 ${lx},240`}
                      fill="url(#jmiStreetLampBeam)"
                      opacity={envTheme.lightOpacity * 0.5}
                    />
                    {/* Road splash — circular pool on ground */}
                    <ellipse
                      cx={lx + 12}
                      cy="240"
                      rx="32"
                      ry="8"
                      fill="#fef08a"
                      opacity={envTheme.lightOpacity * (isRain ? 0.35 : 0.2)}
                    />
                  </>
                )}
                
                {/* Pole base foundation */}
                <rect x={lx - 2.5} y="204" width="5" height="4" fill="#64748b" rx="1" />
                
                {/* Main vertical pole — sturdy with detail */}
                <line x1={lx} y1="204" x2={lx} y2="155" stroke="#94a3b8" strokeWidth="2.5" />
                <line x1={lx} y1="204" x2={lx} y2="155" stroke="#cbd5e1" strokeWidth="1.2" opacity="0.4" />
                
                {/* Pole segments / connection rings */}
                {[192, 180, 168].map((y, i) => (
                  <rect key={i} x={lx - 1.5} y={y} width="3" height="1.5" fill="#64748b" rx="0.3" />
                ))}
                
                {/* Curved lamp arm extending from pole */}
                <path
                  d={`M ${lx},158 Q ${lx},152 ${lx + 8},152 L ${lx + 14},153`}
                  fill="none"
                  stroke="#94a3b8"
                  strokeWidth="2.2"
                />
                <path
                  d={`M ${lx},158 Q ${lx},152 ${lx + 8},152 L ${lx + 14},153`}
                  fill="none"
                  stroke="#cbd5e1"
                  strokeWidth="0.8"
                  opacity="0.4"
                />
                
                {/* Lamp housing — detailed enclosure */}
                <rect x={lx + 7} y="153" width="10" height="4" fill="#0f172a" rx="1" stroke="#374151" strokeWidth="0.5" />
                <rect x={lx + 8} y="153.5" width="8" height="3" fill="#1e293b" rx="0.5" />
                
                {/* Light bulb / LED element inside housing */}
                <ellipse
                  cx={lx + 12}
                  cy="155"
                  rx="3"
                  ry="1.8"
                  fill={showStreetLights ? "#fef08a" : "#475569"}
                  opacity={showStreetLights ? 0.95 : 0.6}
                  filter={showStreetLights ? "url(#jmiGlow)" : undefined}
                />
                
                {/* Glass lens cover reflection */}
                <ellipse
                  cx={lx + 12}
                  cy="154.2"
                  rx="2.5"
                  ry="1"
                  fill="#ffffff"
                  opacity={showStreetLights ? 0.3 : 0.1}
                />
                
                {/* Mounting bracket detail */}
                <rect x={lx + 6.5} y="154.5" width="1" height="2" fill="#6b7280" rx="0.2" />
                
                {/* Rain droplets on lamp glass when raining */}
                {isRain && (
                  <>
                    <circle cx={lx + 10} cy="154" r="0.4" fill="#bae6fd" opacity="0.7" />
                    <circle cx={lx + 13} cy="156" r="0.3" fill="#bae6fd" opacity="0.6" />
                    <circle cx={lx + 14} cy="154.5" r="0.35" fill="#bae6fd" opacity="0.65" />
                  </>
                )}
              </g>
            ))}
          </g>

          {/* ========================================================================= */}
          {/* LAYER 4: Road Surface & Markings                                          */}
          {/* ========================================================================= */}
          <g className="jmi-road-surface">
            <rect x="0" y="204" width="1200" height="36" fill={envTheme.roadColor} />

            {isRain && (
              <rect
                x="0"
                y="204"
                width="1200"
                height="36"
                fill="url(#jmiGlassFacade)"
                opacity="0.4"
              />
            )}

            <g opacity="0.75">
              {[ 520, 535, 550, 565].map((zx) => (
                <rect
                  key={`zebra-${zx}`}
                  x={zx}
                  y="206"
                  width="7"
                  height="32"
                  fill="#ffffff"
                  rx="0.5"
                />
              ))}
            </g>

            <line
              x1="0"
              y1="222"
              x2="1200"
              y2="222"
              stroke={envTheme.roadMarking}
              strokeWidth="1.8"
              strokeDasharray="18,14"
            />
          </g>

          {/* ========================================================================= */}
          {/* LAYER 5: Living People Activities                                         */}
          {/* ========================================================================= */}

          {/* 1. Morning Joggers */}
          {showJoggers && (
            <g className="jmi-animated-joggers">
              <g className="jmi-jogger-track jmi-jogger-fast">
                <circle cx="0" cy="189" r="2.2" fill="#38bdf8" />
                <rect x="-1.5" y="191" width="3" height="5" fill="#0284c7" rx="0.5" />
                <line x1="-1" y1="196" x2="-2" y2="200" stroke="#0369a1" strokeWidth="1.2" />
                <line x1="1" y1="196" x2="2" y2="200" stroke="#0369a1" strokeWidth="1.2" />
              </g>

              <g className="jmi-jogger-track jmi-jogger-med">
                <circle cx="0" cy="189" r="2.2" fill="#f43f5e" />
                <rect x="-1.5" y="191" width="3" height="5" fill="#e11d48" rx="0.5" />
                <line x1="-1" y1="196" x2="2" y2="200" stroke="#9f1239" strokeWidth="1.2" />
                <line x1="1" y1="196" x2="-2" y2="200" stroke="#9f1239" strokeWidth="1.2" />
              </g>
            </g>
          )}

          {/* 2. Morning & Evening Cyclists */}
          {(showMorningCyclists || showEveningCyclists) && (
            <g className="jmi-animated-cyclists">
              <g className="jmi-cyclist-track jmi-cyclist-1">
                <circle cx="-6" cy="201" r="3" fill="none" stroke="#0284c7" strokeWidth="0.8" />
                <circle cx="6" cy="201" r="3" fill="none" stroke="#0284c7" strokeWidth="0.8" />
                <line x1="-6" y1="201" x2="0" y2="198" stroke="#38bdf8" strokeWidth="0.9" />
                <line x1="0" y1="198" x2="6" y2="201" stroke="#38bdf8" strokeWidth="0.9" />
                <line x1="0" y1="198" x2="-2" y2="194" stroke="#38bdf8" strokeWidth="0.9" />
                <circle cx="-1" cy="190" r="2" fill="#fbbf24" />
                <line x1="-1" y1="192" x2="-2" y2="197" stroke="#d97706" strokeWidth="1.5" />
                <line x1="-2" y1="197" x2="0" y2="200" stroke="#1e293b" strokeWidth="1" />
                {showVehicleHeadlights && <circle cx="7" cy="197" r="1" fill="#fef08a" />}
              </g>
            </g>
          )}

          {/* ========================================================================= */}
          {/* LAYER 5B: DETAILED WALKING HUMANS                                        */}
          {/* ========================================================================= */}
          <g className="jmi-animated-pedestrians">

            {/* --- RAIN PEDESTRIAN: umbrella-carrying commuter --- */}
            {isRain && (
              <g className="jmi-walker-track jmi-walker-rain">
                {/* Umbrella canopy */}
                <path d="M -9,183 Q 0,172 9,183" fill="#e11d48" stroke="#b91c1c" strokeWidth="0.5" />
                <line x1="0" y1="183" x2="0" y2="195" stroke="#374151" strokeWidth="0.9" />
                {/* Umbrella handle curve */}
                <path d="M 0,195 Q 0,198 2,198" fill="none" stroke="#374151" strokeWidth="0.9" />
                {/* Head */}
                <circle cx="0" cy="186" r="2.2" fill="#fbbf24" />
                {/* Body - raincoat */}
                <rect x="-1.8" y="188" width="3.6" height="5.5" fill="#0284c7" rx="0.5" />
                {/* Collar */}
                <rect x="-2" y="188" width="4" height="1.2" fill="#0369a1" rx="0.3" />
                {/* Arms holding umbrella */}
                <line x1="-1.5" y1="189" x2="-3" y2="187" stroke="#0369a1" strokeWidth="1" />
                <line x1="1.5" y1="189" x2="3" y2="184" stroke="#0369a1" strokeWidth="1" />
                {/* Bag on shoulder */}
                <rect x="-4.5" y="189" width="3" height="3" fill="#92400e" rx="0.4" />
                {/* Legs - walking stride */}
                <line x1="-0.8" y1="193.5" x2="-2" y2="200" stroke="#1e3a8a" strokeWidth="1.4" />
                <line x1="0.8" y1="193.5" x2="1.5" y2="200" stroke="#1e3a8a" strokeWidth="1.4" />
                {/* Shoes */}
                <ellipse cx="-2" cy="200" rx="2" ry="0.8" fill="#1c1917" />
                <ellipse cx="1.5" cy="200" rx="2" ry="0.8" fill="#1c1917" />
                {/* Rain drops on figure */}
                <line x1="-6" y1="185" x2="-5.5" y2="188" stroke="#bae6fd" strokeWidth="0.6" opacity="0.7" />
                <line x1="5" y1="184" x2="5.5" y2="187" stroke="#bae6fd" strokeWidth="0.6" opacity="0.7" />
              </g>
            )}

            {/* --- BUSINESSMAN: suited man with briefcase + WALKING ANIMATION --- */}
            {!isRain && (isAfternoon || isEvening || isMorning) && (
              <g className="jmi-walker-track jmi-walker-biz">
                {/* Head with hair */}
                <circle cx="0" cy="185" r="2.4" fill="#fcd9b8">
                  <animate attributeName="cy" values="185;184.5;185" dur="1.2s" repeatCount="indefinite" />
                </circle>
                <rect x="-2.4" y="182.5" width="4.8" height="2.2" fill="#1c1917" rx="1.2">
                  <animate attributeName="y" values="182.5;182;182.5" dur="1.2s" repeatCount="indefinite" />
                </rect>
                
                {/* White collar / tie */}
                <rect x="-0.8" y="187" width="1.6" height="1.5" fill="#ffffff" rx="0.2">
                  <animate attributeName="y" values="187;186.6;187" dur="1.2s" repeatCount="indefinite" />
                </rect>
                <polygon points="0,188.5 -0.4,191 0.4,191" fill="#dc2626">
                  <animateTransform attributeName="transform" type="translate" values="0,0; 0,-0.3; 0,0" dur="1.2s" repeatCount="indefinite" />
                </polygon>
                
                {/* Suit jacket with body bounce */}
                <rect x="-2.2" y="187.4" width="4.4" height="6" fill="#1e3a8a" rx="0.5">
                  <animate attributeName="y" values="187.4;187;187.4" dur="1.2s" repeatCount="indefinite" />
                </rect>
                
                {/* Jacket lapels */}
                <polygon points="-2.2,187.4 0,189 0,187.4" fill="#1e40af" opacity="0.7">
                  <animateTransform attributeName="transform" type="translate" values="0,0; 0,-0.3; 0,0" dur="1.2s" repeatCount="indefinite" />
                </polygon>
                <polygon points="2.2,187.4 0,189 0,187.4" fill="#1e40af" opacity="0.7">
                  <animateTransform attributeName="transform" type="translate" values="0,0; 0,-0.3; 0,0" dur="1.2s" repeatCount="indefinite" />
                </polygon>
                
                {/* Pocket square */}
                <rect x="-2" y="188" width="1.2" height="0.8" fill="#ffffff" rx="0.1">
                  <animate attributeName="y" values="188;187.6;188" dur="1.2s" repeatCount="indefinite" />
                </rect>
                
                {/* Left arm — SWINGING FORWARD/BACK */}
                <line x1="-2" y1="188.5" x2="-4" y2="193" stroke="#1e3a8a" strokeWidth="1.3">
                  <animate attributeName="x2" values="-4;-3;-4" dur="1.2s" repeatCount="indefinite" />
                  <animate attributeName="y2" values="193;191;193" dur="1.2s" repeatCount="indefinite" />
                </line>
                <line x1="-4" y1="193" x2="-3.5" y2="195" stroke="#fcd9b8" strokeWidth="1">
                  <animate attributeName="x1" values="-4;-3;-4" dur="1.2s" repeatCount="indefinite" />
                  <animate attributeName="y1" values="193;191;193" dur="1.2s" repeatCount="indefinite" />
                  <animate attributeName="x2" values="-3.5;-2.5;-3.5" dur="1.2s" repeatCount="indefinite" />
                </line>
                
                {/* Right arm — holding briefcase, SLIGHT SWING */}
                <line x1="2" y1="188.5" x2="4" y2="193" stroke="#1e3a8a" strokeWidth="1.3">
                  <animate attributeName="x2" values="4;4.5;4" dur="1.2s" repeatCount="indefinite" />
                  <animate attributeName="y2" values="193;194;193" dur="1.2s" repeatCount="indefinite" />
                </line>
                
                {/* Briefcase with swing */}
                <rect x="3.2" y="193" width="3.8" height="2.8" fill="#92400e" rx="0.5" stroke="#78350f" strokeWidth="0.4">
                  <animate attributeName="x" values="3.2;3.7;3.2" dur="1.2s" repeatCount="indefinite" />
                  <animate attributeName="y" values="193;194;193" dur="1.2s" repeatCount="indefinite" />
                </rect>
                <rect x="4.3" y="192.3" width="1.6" height="0.8" fill="none" stroke="#78350f" strokeWidth="0.5" rx="0.3">
                  <animate attributeName="x" values="4.3;4.8;4.3" dur="1.2s" repeatCount="indefinite" />
                  <animate attributeName="y" values="192.3;193.3;192.3" dur="1.2s" repeatCount="indefinite" />
                </rect>
                <line x1="3.2" y1="194.4" x2="7" y2="194.4" stroke="#78350f" strokeWidth="0.4">
                  <animate attributeName="x1" values="3.2;3.7;3.2" dur="1.2s" repeatCount="indefinite" />
                  <animate attributeName="x2" values="7;7.5;7" dur="1.2s" repeatCount="indefinite" />
                  <animate attributeName="y1" values="194.4;195.4;194.4" dur="1.2s" repeatCount="indefinite" />
                  <animate attributeName="y2" values="194.4;195.4;194.4" dur="1.2s" repeatCount="indefinite" />
                </line>
                
                {/* Left leg — STEP FORWARD/BACK */}
                <line x1="-1" y1="193.4" x2="-2" y2="200" stroke="#0f172a" strokeWidth="1.6">
                  <animate attributeName="x2" values="-2;-1;-2" dur="1.2s" repeatCount="indefinite" />
                </line>
                {/* Right leg — STEP OPPOSITE */}
                <line x1="1" y1="193.4" x2="2.5" y2="200" stroke="#0f172a" strokeWidth="1.6">
                  <animate attributeName="x2" values="2.5;1.5;2.5" dur="1.2s" repeatCount="indefinite" />
                </line>
                
                {/* Shoes with step */}
                <ellipse cx="-2.2" cy="200.2" rx="2.2" ry="0.9" fill="#1c1917">
                  <animate attributeName="cx" values="-2.2;-1.2;-2.2" dur="1.2s" repeatCount="indefinite" />
                </ellipse>
                <ellipse cx="2.6" cy="200.2" rx="2.2" ry="0.9" fill="#1c1917">
                  <animate attributeName="cx" values="2.6;1.6;2.6" dur="1.2s" repeatCount="indefinite" />
                </ellipse>
              </g>
            )}

            {/* --- WOMAN: dress, handbag, heels --- */}
            {!isRain && (isAfternoon || isEvening || isMorning) && (
              <g className="jmi-walker-track jmi-walker-woman">
                {/* Hair */}
                <ellipse cx="0" cy="184" rx="2.8" ry="3.2" fill="#7c3aed" />
                {/* Head */}
                <circle cx="0" cy="184.5" r="2.2" fill="#fcd9b8" />
                {/* Hair flowing behind */}
                <path d="M 2.2,183 Q 4,185 3.5,188" stroke="#7c3aed" strokeWidth="1.2" fill="none" />
                {/* Dress top */}
                <path d="M -2.5,187 Q 0,185.5 2.5,187 L 2,193 Q 0,194 -2,193 Z" fill="#ec4899" />
                {/* Dress collar detail */}
                <path d="M -1.2,187 Q 0,186.5 1.2,187" stroke="#f9a8d4" strokeWidth="0.5" fill="none" />
                {/* Skirt flare */}
                <path d="M -2,193 Q -4,196 -3.5,200 L 3.5,200 Q 4,196 2,193 Q 0,194 -2,193 Z" fill="#f472b6" />
                {/* Left arm — swinging */}
                <line x1="-2.5" y1="188" x2="-4.5" y2="192" stroke="#ec4899" strokeWidth="1.2" />
                <line x1="-4.5" y1="192" x2="-4" y2="194.5" stroke="#fcd9b8" strokeWidth="1" />
                {/* Right arm — handbag */}
                <line x1="2.5" y1="188" x2="4.5" y2="192" stroke="#ec4899" strokeWidth="1.2" />
                {/* Handbag */}
                <rect x="4" y="192" width="3.5" height="2.5" fill="#fbbf24" rx="0.6" stroke="#d97706" strokeWidth="0.4" />
                <path d="M 4.8,192 Q 5.75,190.5 6.7,192" fill="none" stroke="#d97706" strokeWidth="0.6" />
                {/* Heels */}
                <ellipse cx="-2.5" cy="200.5" rx="1.8" ry="0.7" fill="#be185d" />
                <line x1="-2.5" y1="200.5" x2="-2.5" y2="202" stroke="#be185d" strokeWidth="0.8" />
                <ellipse cx="2.5" cy="200.5" rx="1.8" ry="0.7" fill="#be185d" />
                <line x1="2.5" y1="200.5" x2="2.5" y2="202" stroke="#be185d" strokeWidth="0.8" />
              </g>
            )}

            {/* --- COUPLE: man + woman walking together, man's arm around woman --- */}
            {!isRain && (isAfternoon || isEvening) && (
              <g className="jmi-walker-track jmi-walker-couple">
                {/* ── Woman (left) ── */}
                {/* Hair */}
                <ellipse cx="-4" cy="184" rx="2.4" ry="2.8" fill="#b45309" />
                <circle cx="-4" cy="184.5" r="2" fill="#fcd9b8" />
                {/* Dress */}
                <path d="M -6,187 Q -4,186 -2,187 L -2.5,193 Q -4,194 -5.5,193 Z" fill="#06b6d4" />
                <path d="M -5.5,193 Q -7,196 -6.5,200 L -1.5,200 Q -2,196 -2.5,193 Z" fill="#0891b2" />
                {/* Legs */}
                <line x1="-5" y1="193" x2="-5.8" y2="200" stroke="#0891b2" strokeWidth="1.5" />
                <line x1="-3" y1="193" x2="-2.2" y2="200" stroke="#0891b2" strokeWidth="1.5" />
                <ellipse cx="-5.8" cy="200.3" rx="1.6" ry="0.7" fill="#1c1917" />
                <ellipse cx="-2.2" cy="200.3" rx="1.6" ry="0.7" fill="#1c1917" />

                {/* ── Man (right) ── */}
                {/* Head */}
                <circle cx="4" cy="184.5" r="2.3" fill="#fcd9b8" />
                <rect x="1.8" y="182" width="4.4" height="1.8" fill="#0f172a" rx="0.8" />
                {/* Suit */}
                <rect x="1.8" y="187" width="4.4" height="6" fill="#374151" rx="0.5" />
                {/* His arm around her shoulder */}
                <line x1="1.8" y1="188" x2="-1.5" y2="188" stroke="#374151" strokeWidth="1.4" />
                {/* Free arm */}
                <line x1="6.2" y1="188.5" x2="7.8" y2="193" stroke="#374151" strokeWidth="1.3" />
                <line x1="7.8" y1="193" x2="7.4" y2="195.5" stroke="#fcd9b8" strokeWidth="1" />
                {/* Trousers */}
                <line x1="3" y1="193" x2="2.2" y2="200" stroke="#1f2937" strokeWidth="1.6" />
                <line x1="5" y1="193" x2="5.8" y2="200" stroke="#1f2937" strokeWidth="1.6" />
                <ellipse cx="2.2" cy="200.3" rx="2" ry="0.8" fill="#1c1917" />
                <ellipse cx="5.8" cy="200.3" rx="2" ry="0.8" fill="#1c1917" />

                {/* Heart spark between couple */}
                <text x="-0.5" y="182" fontSize="3" fill="#f43f5e" textAnchor="middle" opacity="0.85" className="jmi-couple-heart">♥</text>
              </g>
            )}

            {/* --- NIGHT SECURITY GUARD: standing at building entrance --- */}
            {isNight && (
              <g className="jmi-walker-track jmi-walker-guard">
                {/* Head */}
                <circle cx="0" cy="185" r="2.2" fill="#fcd9b8" />
                {/* Guard cap */}
                <rect x="-2.5" y="182.5" width="5" height="1.5" fill="#1e3a8a" rx="0.3" />
                <rect x="-3" y="183.8" width="6" height="0.6" fill="#1e3a8a" rx="0.2" />
                {/* Uniform */}
                <rect x="-2.2" y="187.2" width="4.4" height="6" fill="#1e3a8a" rx="0.5" />
                {/* Badge */}
                <rect x="-0.8" y="188.2" width="1.6" height="1.2" fill="#f59e0b" rx="0.2" />
                {/* Belt */}
                <rect x="-2.4" y="192" width="4.8" height="0.8" fill="#0f172a" />
                {/* Arms — at rest */}
                <line x1="-2.2" y1="188.5" x2="-3.5" y2="193" stroke="#1e3a8a" strokeWidth="1.3" />
                <line x1="2.2" y1="188.5" x2="3.5" y2="193" stroke="#1e3a8a" strokeWidth="1.3" />
                {/* Torch / flashlight in right hand */}
                <rect x="3.2" y="193" width="1" height="3" fill="#d97706" rx="0.3" />
                <circle cx="3.7" cy="193" r="1" fill="#fef08a" opacity="0.9"
                  filter={showVehicleHeadlights ? "url(#jmiGlow)" : undefined} />
                {/* Trousers */}
                <line x1="-1" y1="193.2" x2="-1.5" y2="200" stroke="#0f172a" strokeWidth="1.6" />
                <line x1="1" y1="193.2" x2="1.5" y2="200" stroke="#0f172a" strokeWidth="1.6" />
                <ellipse cx="-1.6" cy="200.2" rx="2" ry="0.8" fill="#1c1917" />
                <ellipse cx="1.6" cy="200.2" rx="2" ry="0.8" fill="#1c1917" />
              </g>
            )}

            {/* --- SHOPPERS: Going to and coming from BUYER BUILDING with shopping bags --- */}
            {/* ALWAYS VISIBLE - Shopping happens all day! */}
            <>
              {/* Shopper 1: Going TO buyer building (walking leftward) - NO BAGS */}
              <g className="jmi-walker-track jmi-shopper-to-mall">
                <circle cx="0" cy="186" r="2.5" fill="#fcd9b8" />
                <ellipse cx="0" cy="192" rx="3.2" ry="5" fill="#3b82f6" />
                <line x1="-1.8" y1="197" x2="-2.5" y2="200.5" stroke="#1e40af" strokeWidth="2" />
                <line x1="1.8" y1="197" x2="2.5" y2="200.5" stroke="#1e40af" strokeWidth="2" />
                <ellipse cx="-2.5" cy="200.8" rx="2" ry="0.8" fill="#1c1917" />
                <ellipse cx="2.5" cy="200.8" rx="2" ry="0.8" fill="#1c1917" />
                <line x1="-3.2" y1="190" x2="-5" y2="194" stroke="#1e3a8a" strokeWidth="1.5" />
                <line x1="3.2" y1="190" x2="5" y2="194" stroke="#1e3a8a" strokeWidth="1.5" />
              </g>

              {/* Shopper 2: Coming FROM buyer building with RED shopping bag */}
              <g className="jmi-walker-track jmi-shopper-from-mall">
                <circle cx="0" cy="186" r="2.5" fill="#fcd9b8" />
                <ellipse cx="0" cy="192" rx="3.2" ry="5" fill="#ec4899" />
                <line x1="-1.8" y1="197" x2="-2.5" y2="200.5" stroke="#be185d" strokeWidth="2" />
                <line x1="1.8" y1="197" x2="2.5" y2="200.5" stroke="#be185d" strokeWidth="2" />
                <ellipse cx="-2.5" cy="200.8" rx="2" ry="0.8" fill="#1c1917" />
                <ellipse cx="2.5" cy="200.8" rx="2" ry="0.8" fill="#1c1917" />
                {/* Left arm with LARGE RED shopping bag */}
                <line x1="-3.2" y1="190" x2="-6" y2="194" stroke="#9f1239" strokeWidth="1.5" />
                <rect x="-8" y="194" width="5" height="5.5" fill="#dc2626" rx="0.6" stroke="#7f1d1d" strokeWidth="0.4" />
                <line x1="-7" y1="194" x2="-4" y2="194" stroke="#fca5a5" strokeWidth="0.8" />
                {/* Right arm swinging */}
                <line x1="3.2" y1="190" x2="5" y2="194" stroke="#9f1239" strokeWidth="1.5" />
              </g>

              {/* Shopper 3: Coming FROM buyer building with TWO LARGE bags */}
              <g className="jmi-walker-track jmi-shopper-from-mall-2">
                <circle cx="0" cy="186" r="2.5" fill="#fcd9b8" />
                <ellipse cx="0" cy="192" rx="3.2" ry="5" fill="#10b981" />
                <line x1="-1.8" y1="197" x2="-2.5" y2="200.5" stroke="#059669" strokeWidth="2" />
                <line x1="1.8" y1="197" x2="2.5" y2="200.5" stroke="#059669" strokeWidth="2" />
                <ellipse cx="-2.5" cy="200.8" rx="2" ry="0.8" fill="#1c1917" />
                <ellipse cx="2.5" cy="200.8" rx="2" ry="0.8" fill="#1c1917" />
                {/* Left arm with BLUE bag */}
                <line x1="-3.2" y1="190" x2="-6" y2="194" stroke="#047857" strokeWidth="1.5" />
                <rect x="-8" y="194" width="5" height="5.5" fill="#0ea5e9" rx="0.6" stroke="#075985" strokeWidth="0.4" />
                <line x1="-7" y1="194" x2="-4" y2="194" stroke="#7dd3fc" strokeWidth="0.8" />
                {/* Right arm with GOLDEN bag */}
                <line x1="3.2" y1="190" x2="6" y2="194" stroke="#047857" strokeWidth="1.5" />
                <rect x="5" y="194" width="5" height="5.5" fill="#f59e0b" rx="0.6" stroke="#92400e" strokeWidth="0.4" />
                <line x1="6" y1="194" x2="9" y2="194" stroke="#fde047" strokeWidth="0.8" />
              </g>

              {/* Shopper 4: Going TO mall - another customer */}
              <g className="jmi-walker-track jmi-shopper-to-mall-2">
                <circle cx="0" cy="186" r="2.5" fill="#fcd9b8" />
                <ellipse cx="0" cy="192" rx="3.2" ry="5" fill="#8b5cf6" />
                <line x1="-1.8" y1="197" x2="-2.5" y2="200.5" stroke="#6d28d9" strokeWidth="2" />
                <line x1="1.8" y1="197" x2="2.5" y2="200.5" stroke="#6d28d9" strokeWidth="2" />
                <ellipse cx="-2.5" cy="200.8" rx="2" ry="0.8" fill="#1c1917" />
                <ellipse cx="2.5" cy="200.8" rx="2" ry="0.8" fill="#1c1917" />
                <line x1="-3.2" y1="190" x2="-5" y2="194" stroke="#5b21b6" strokeWidth="1.5" />
                <line x1="3.2" y1="190" x2="5" y2="194" stroke="#5b21b6" strokeWidth="1.5" />
              </g>

              {/* Shopper 5: Coming FROM mall with SILVER bag */}
              <g className="jmi-walker-track jmi-shopper-from-mall-3">
                <circle cx="0" cy="186" r="2.5" fill="#fcd9b8" />
                <ellipse cx="0" cy="192" rx="3.2" ry="5" fill="#fbbf24" />
                <line x1="-1.8" y1="197" x2="-2.5" y2="200.5" stroke="#d97706" strokeWidth="2" />
                <line x1="1.8" y1="197" x2="2.5" y2="200.5" stroke="#d97706" strokeWidth="2" />
                <ellipse cx="-2.5" cy="200.8" rx="2" ry="0.8" fill="#1c1917" />
                <ellipse cx="2.5" cy="200.8" rx="2" ry="0.8" fill="#1c1917" />
                {/* Arm with PLATINUM/SILVER bag */}
                <line x1="-3.2" y1="190" x2="-6" y2="194" stroke="#b45309" strokeWidth="1.5" />
                <rect x="-8" y="194" width="5" height="5.5" fill="#e5e7eb" rx="0.6" stroke="#9ca3af" strokeWidth="0.4" />
                <line x1="-7" y1="194" x2="-4" y2="194" stroke="#ffffff" strokeWidth="0.8" />
                <line x1="3.2" y1="190" x2="5" y2="194" stroke="#b45309" strokeWidth="1.5" />
              </g>
            </>
          </g>

          {/* ========================================================================= */}
          {/* LAYER 6: Realistic Vehicles (JMI Cargo Truck, Sports Car, Moto, Sedan, Police) */}
          {/* ========================================================================= */}

          {/* --- VEHICLE 1: JMI SECURE CARGO TRUCK (ALWAYS VISIBLE WITH "JMI" BRANDING) --- */}
          <g className="jmi-vehicle-track jmi-truck-flow">
            <ellipse cx="40" cy="226" rx="42" ry="4" fill="rgba(0,0,0,0.4)" />

            {showVehicleHeadlights && (
              <>
                {/* Side horizontal beam */}
                <polygon
                  points="88,220 160,212 160,230 88,224"
                  fill="url(#jmiHeadlightBeam)"
                  opacity={isNight ? 0.5 : 0.3}
                />
                {/* Angled road projection — fan hitting tarmac surface */}
                <polygon
                  points="87,223 87,226 170,240 145,225"
                  fill="url(#jmiRoadBeamWhite)"
                  opacity={isNight ? 0.75 : 0.4}
                />
              </>
            )}

            {/* Cargo Box with Official JMI Logo */}
            <rect
              x="0"
              y="207"
              width="62"
              height="16"
              fill="#ffffff"
              rx="1.5"
              stroke="#b91c1c"
              strokeWidth="0.8"
            />
            <image
              href={jmiLogoImg}
              x="3"
              y="209"
              width="56"
              height="12"
              preserveAspectRatio="xMidYMid meet"
            />

            {/* Truck Cabin */}
            <path
              d="M 62,210 L 78,210 Q 84,210 86,215 L 88,223 L 62,223 Z"
              fill="#1e3a8a"
              stroke="#38bdf8"
              strokeWidth="0.5"
            />
            <path d="M 68,212 L 77,212 L 83,218 L 68,218 Z" fill="#93c5fd" opacity="0.85" />
            <rect x="76" y="213" width="1.5" height="3.5" fill="#f59e0b" />

            <rect x="86" y="219" width="2" height="3.5" fill="#e2e8f0" />
            <circle
              cx="87"
              cy="221"
              r="1.5"
              fill={showVehicleHeadlights ? "#fef08a" : "#cbd5e1"}
              filter={showVehicleHeadlights ? "url(#jmiGlow)" : undefined}
            />

            <rect
              x="-0.5"
              y="219"
              width="1.2"
              height="3"
              fill={showVehicleHeadlights ? "#ef4444" : "#991b1b"}
              filter={isNight ? "url(#jmiGlow)" : undefined}
            />

            {/* Wheels */}
            <g>
              <circle cx="12" cy="224" r="3.5" fill="#1e293b" />
              <circle cx="12" cy="224" r="1.5" fill="#94a3b8" />
              <circle cx="22" cy="224" r="3.5" fill="#1e293b" />
              <circle cx="22" cy="224" r="1.5" fill="#94a3b8" />
              <circle cx="76" cy="224" r="3.5" fill="#1e293b" />
              <circle cx="76" cy="224" r="1.5" fill="#94a3b8" />
            </g>
          </g>

          {/* --- VEHICLE 2: LUXURY GOLDEN SPORTS CAR (AFTERNOON/EVENING) --- */}
          {!isStorm && (isAfternoon || isEvening) && (
            <g className="jmi-vehicle-track jmi-sportscar-flow">
              <ellipse cx="20" cy="233" rx="22" ry="3" fill="rgba(0,0,0,0.4)" />

              {showVehicleHeadlights && (
                <>
                  <polygon
                    points="42,230 100,224 100,238 42,233"
                    fill="url(#jmiHeadlightBeam)"
                    opacity={0.45}
                  />
                  {/* Road projection fan */}
                  <polygon
                    points="41,232 41,235 115,242 95,234"
                    fill="url(#jmiRoadBeamGold)"
                    opacity={isNight ? 0.8 : 0.45}
                  />
                </>
              )}

              <path
                d="M 0,230 L 6,226 L 16,223 L 28,223 L 36,227 L 42,230 L 42,233 L 0,233 Z"
                fill="url(#jmiSportsCarGrad)"
                stroke="#b45309"
                strokeWidth="0.4"
              />
              <polygon points="12,224 18,220 26,220 32,224 12,224" fill="#0f172a" opacity="0.85" />
              <polygon points="1,226 3,222 7,222 5,226" fill="#0f172a" />

              <circle cx="8" cy="232" r="2.8" fill="#020617" />
              <circle cx="8" cy="232" r="1.4" fill="#f59e0b" />
              <circle cx="34" cy="232" r="2.8" fill="#020617" />
              <circle cx="34" cy="232" r="1.4" fill="#f59e0b" />

              <text
                x="22"
                y="229"
                fontSize="2.8"
                fontWeight="900"
                fontFamily="system-ui, sans-serif"
                fill="#ffffff"
                textAnchor="middle"
                letterSpacing="0.3"
              >
                JMI
              </text>

              <circle
                cx="41.5"
                cy="230"
                r="1.2"
                fill={showVehicleHeadlights ? "#fef08a" : "#ffffff"}
                filter={showVehicleHeadlights ? "url(#jmiGlow)" : undefined}
              />
              <rect
                x="-0.5"
                y="229"
                width="1.2"
                height="2"
                fill="#ef4444"
                filter={isNight ? "url(#jmiGlow)" : undefined}
              />
            </g>
          )}

          {/* --- VEHICLE 3: INDIAN TWO-WHEELER / COMMUTER MOTORCYCLE (MORNING) --- */}
          {!isStorm && isMorning && (
            <g className="jmi-vehicle-track jmi-motorcycle-flow">
              <ellipse cx="6" cy="232" rx="9" ry="2" fill="rgba(0,0,0,0.35)" />
              <circle cx="0" cy="230" r="2.5" fill="#1e293b" />
              <circle cx="0" cy="230" r="1" fill="#cbd5e1" />
              <circle cx="12" cy="230" r="2.5" fill="#1e293b" />
              <circle cx="12" cy="230" r="1" fill="#cbd5e1" />
              <polygon points="0,229 6,226 12,229 7,229" fill="#dc2626" />
              <circle cx="5" cy="222" r="1.8" fill="#1e293b" />
              <line x1="5" y1="223" x2="6" y2="228" stroke="#1e293b" strokeWidth="1.5" />
              <circle
                cx="13"
                cy="228"
                r="1"
                fill={showVehicleHeadlights ? "#fef08a" : "#cbd5e1"}
              />
            </g>
          )}

          {/* --- VEHICLE 4: EXECUTIVE B2B CAR WITH JMI LOGO --- */}
          {!isStorm && (
            <g className="jmi-vehicle-track jmi-sedan-flow">
              <ellipse cx="16" cy="216" rx="18" ry="2.5" fill="rgba(0,0,0,0.3)" />
              {showVehicleHeadlights && (
                <>
                  <polygon
                    points="32,213 75,208 75,220 32,216"
                    fill="url(#jmiHeadlightBeam)"
                    opacity="0.4"
                  />
                  {/* Road projection fan */}
                  <polygon
                    points="32,215 32,218 100,228 78,218"
                    fill="url(#jmiRoadBeamWhite)"
                    opacity={isNight ? 0.7 : 0.35}
                  />
                </>
              )}
              <path
                d="M 0,213 L 6,208 L 22,208 L 29,211 L 33,214 L 0,214 Z"
                fill="#334155"
                stroke="#64748b"
                strokeWidth="0.4"
              />
              <polygon points="8,209 13,209 13,212 7,212" fill="#bae6fd" opacity="0.8" />
              <polygon points="15,209 21,209 24,212 15,212" fill="#bae6fd" opacity="0.8" />
              <text x="17" y="213.5" fontSize="2" fontWeight="800" fill="#f59e0b">JMI</text>
              <circle cx="6" cy="215" r="2.2" fill="#0f172a" />
              <circle cx="26" cy="215" r="2.2" fill="#0f172a" />
              <circle
                cx="33"
                cy="213"
                r="1"
                fill={showVehicleHeadlights ? "#fef08a" : "#e2e8f0"}
              />
              <rect
                x="-0.5"
                y="212"
                width="1"
                height="2"
                fill={showVehicleHeadlights ? "#ef4444" : "#7f1d1d"}
              />
            </g>
          )}

          {/* --- VEHICLE 5: NIGHT POLICE PATROL CAR (LATE NIGHT ONLY) --- */}
          {isLateNight && (
            <g className="jmi-vehicle-track jmi-police-patrol">
              <ellipse cx="15" cy="231" rx="17" ry="2.8" fill="rgba(0,0,0,0.4)" />
              
              {/* Police light reflections on road — RED and BLUE alternating glow */}
              <ellipse cx="13" cy="240" rx="12" ry="4" fill="#ef4444" opacity="0.4" className="jmi-police-red-reflect" />
              <ellipse cx="17" cy="240" rx="12" ry="4" fill="#3b82f6" opacity="0.4" className="jmi-police-blue-reflect" />
              
              {/* Side horizontal + angled road beam */}
              <>
                <polygon
                  points="30,227 80,222 80,234 30,229"
                  fill="url(#jmiHeadlightBeam)"
                  opacity={0.5}
                />
                {/* Road projection fan */}
                <polygon
                  points="30,229 30,232 95,240 75,232"
                  fill="url(#jmiRoadBeamBlue)"
                  opacity={0.8}
                />
              </>
              <path
                d="M 0,227 L 6,222 L 20,222 L 27,225 L 31,228 L 0,228 Z"
                fill="#f8fafc"
                stroke="#0f172a"
                strokeWidth="0.5"
              />
              <rect x="8" y="225" width="14" height="3" fill="#1e3a8a" />
              <text
                x="15"
                y="227.5"
                fontSize="2.2"
                fontWeight="900"
                fill="#ffffff"
                textAnchor="middle"
              >
                POLICE
              </text>
              <polygon points="7,223 12,223 12,225 6,225" fill="#38bdf8" opacity="0.8" />
              <polygon points="14,223 19,223 22,225 14,225" fill="#38bdf8" opacity="0.8" />
              <g>
                <rect x="11" y="220" width="8" height="2" fill="#1e293b" rx="0.5" />
                <circle cx="13" cy="221" r="1.3" fill="#ef4444" className="jmi-police-red" />
                <circle cx="17" cy="221" r="1.3" fill="#3b82f6" className="jmi-police-blue" />
              </g>
              <circle cx="5" cy="229" r="2.4" fill="#0f172a" />
              <circle cx="5" cy="229" r="1" fill="#e2e8f0" />
              <circle cx="24" cy="229" r="2.4" fill="#0f172a" />
              <circle cx="24" cy="229" r="1" fill="#e2e8f0" />
              <circle cx="31" cy="227" r="1.2" fill="#fef08a" />
            </g>
          )}

          {/* --- VEHICLE 6: GOLDEN LAMBORGHINI WITH EXHAUST FIRE (EVENING/NIGHT) --- */}
          {!isStorm && (isEvening || isNight) && (
            <g className="jmi-vehicle-track jmi-lambo-flow">
              {/* Ground shadow */}
              <ellipse cx="26" cy="236" rx="30" ry="2.5" fill="rgba(0,0,0,0.45)" />

              {/* ── Road light beam projected forward at angle ── */}
              {showVehicleHeadlights && (
                <polygon
                  points="52,229 52,232 130,218 130,237"
                  fill="url(#jmiRoadBeamGold)"
                  opacity={isNight ? 0.9 : 0.55}
                />
              )}

              {/* ── Exhaust flame & glow (rear, left side) ── */}
              <g className="jmi-lambo-exhaust">
                {/* Outer orange cone */}
                <polygon points="-4,229 -4,232 -16,230.5" fill="#f97316" opacity="0.9" />
                {/* Inner hot white/yellow core */}
                <polygon points="-4,230 -4,231.5 -11,230.5" fill="#fef08a" opacity="0.95" />
                {/* Exhaust glow halo */}
                <ellipse cx="-8" cy="230.5" rx="5" ry="2" fill="#f97316" opacity="0.4"
                  filter="url(#jmiGlow)" />
                {/* Second twin pipe */}
                <polygon points="-2,229.5 -2,231 -12,230.2" fill="#fb923c" opacity="0.7" />
                {/* Shimmer particles */}
                <circle cx="-14" cy="229" r="0.8" fill="#fde047" opacity="0.8" className="jmi-exhaust-spark s1" />
                <circle cx="-17" cy="231" r="0.6" fill="#f97316" opacity="0.7" className="jmi-exhaust-spark s2" />
                <circle cx="-13" cy="232" r="0.7" fill="#ef4444" opacity="0.6" className="jmi-exhaust-spark s3" />
              </g>

              {/* ── Exhaust pipes (twin round) ── */}
              <ellipse cx="-3" cy="229.8" rx="1.2" ry="0.7" fill="#374151" stroke="#6b7280" strokeWidth="0.3" />
              <ellipse cx="-1" cy="229.8" rx="1.2" ry="0.7" fill="#374151" stroke="#6b7280" strokeWidth="0.3" />

              {/* ── Car body — wedge Lamborghini silhouette ── */}
              {/* Low slung body */}
              <path
                d="M -2,231 L 4,225 L 15,222 L 36,222 L 46,226 L 52,230 L 52,233 L -2,233 Z"
                fill="#d97706"
                stroke="#92400e"
                strokeWidth="0.5"
              />
              {/* Body highlight stripe — chrome side line */}
              <path
                d="M 2,226.5 L 15,223 L 36,223 L 46,227"
                fill="none"
                stroke="#fef08a"
                strokeWidth="0.8"
                opacity="0.7"
              />
              {/* Carbon-fibre side sill */}
              <rect x="2" y="231.5" width="48" height="1.2" fill="#1c1917" rx="0.3" />

              {/* ── Roof / cabin ── */}
              <polygon points="12,226 17,220 34,220 40,226 12,226" fill="#0f172a" />
              {/* Windscreen */}
              <polygon points="14,225.5 18,220.5 32,220.5 37,225.5 14,225.5" fill="#1e3a8a" opacity="0.85" />
              {/* Windscreen glint */}
              <line x1="19" y1="221" x2="16" y2="225" stroke="#bae6fd" strokeWidth="0.5" opacity="0.5" />

              {/* ── Air vent / side intake ── */}
              <rect x="38" y="225" width="6" height="3" fill="#1c1917" rx="0.5" />
              <line x1="39" y1="225" x2="39" y2="228" stroke="#374151" strokeWidth="0.4" />
              <line x1="41" y1="225" x2="41" y2="228" stroke="#374151" strokeWidth="0.4" />
              <line x1="43" y1="225" x2="43" y2="228" stroke="#374151" strokeWidth="0.4" />

              {/* ── Lamborghini front splitter ── */}
              <rect x="48" y="231" width="5" height="1" fill="#1c1917" rx="0.3" />
              <rect x="49" y="232" width="3" height="0.6" fill="#374151" />

              {/* ── Door panel line ── */}
              <path d="M 14,226 Q 22,224 34,224 L 34,231 Q 22,231 14,231 Z"
                fill="none" stroke="#b45309" strokeWidth="0.4" opacity="0.6" />

              {/* ── Lamborghini badge / "L" on door ── */}
              <text x="24" y="229.5" fontSize="3" fontWeight="900"
                fontFamily="system-ui, sans-serif" fill="#fef08a"
                textAnchor="middle" letterSpacing="0.5">
                LAMBO
              </text>

              {/* ── Headlight (front, angular) ── */}
              <polygon points="48,228 52,226 52,229 49,229" fill="#fef08a" opacity="0.9"
                filter={showVehicleHeadlights ? "url(#jmiGlow)" : undefined} />
              <line x1="52" y1="227" x2="52" y2="229" stroke="#fef08a" strokeWidth="0.8"
                filter={showVehicleHeadlights ? "url(#jmiGlow)" : undefined} />

              {/* ── Tail light (rear, angular red slash) ── */}
              <polygon points="-2,230 1,228 1,231 -2,232" fill="#ef4444" opacity="0.95"
                filter={isNight ? "url(#jmiGlow)" : undefined} />
              <line x1="-1" y1="228.5" x2="0" y2="231.5" stroke="#fca5a5" strokeWidth="0.5" opacity="0.7" />

              {/* ── Wheels (low-profile with rim detail) ── */}
              {/* Rear wheel */}
              <circle cx="10" cy="233.5" r="4" fill="#0f172a" />
              <circle cx="10" cy="233.5" r="2.8" fill="#1c1917" stroke="#374151" strokeWidth="0.4" />
              <circle cx="10" cy="233.5" r="1.5" fill="#9ca3af" />
              {/* Rim spokes */}
              {[0,60,120,180,240,300].map((angle, i) => {
                const rad = (angle * Math.PI) / 180;
                return <line key={i}
                  x1={10} y1={233.5}
                  x2={10 + 2.5 * Math.cos(rad)}
                  y2={233.5 + 2.5 * Math.sin(rad)}
                  stroke="#6b7280" strokeWidth="0.5" />;
              })}
              {/* Front wheel */}
              <circle cx="40" cy="233.5" r="4" fill="#0f172a" />
              <circle cx="40" cy="233.5" r="2.8" fill="#1c1917" stroke="#374151" strokeWidth="0.4" />
              <circle cx="40" cy="233.5" r="1.5" fill="#9ca3af" />
              {[0,60,120,180,240,300].map((angle, i) => {
                const rad = (angle * Math.PI) / 180;
                return <line key={i}
                  x1={40} y1={233.5}
                  x2={40 + 2.5 * Math.cos(rad)}
                  y2={233.5 + 2.5 * Math.sin(rad)}
                  stroke="#6b7280" strokeWidth="0.5" />;
              })}

              {/* ── Brake caliper flash (golden, visible inside rim) ── */}
              <path d="M 8,231 Q 10,230 12,231" stroke="#fbbf24" strokeWidth="0.7" fill="none" opacity="0.8" />
              <path d="M 38,231 Q 40,230 42,231" stroke="#fbbf24" strokeWidth="0.7" fill="none" opacity="0.8" />
            </g>
          )}
        </svg>

        {/* Top Overlay: Weather Chip + Greeting Text */}
        <div className="jmi-top-overlay">
          <div className="jmi-weather-chip">
            <span className="jmi-weather-icon-wrap">
              <WeatherIconSVG code={weather.code} size={15} color="#e2e8f0" />
            </span>
            {!weather.loading && weather.temp !== null ? (
              <>
                <span className="jmi-chip-temp">{weather.temp}°C</span>
                <span className="jmi-chip-divider">·</span>
                <span className="jmi-chip-desc">{weather.desc}</span>
                <span className="jmi-chip-divider">·</span>
                <span className="jmi-chip-metric">💧 {weather.humidity}%</span>
                <span className="jmi-chip-divider">·</span>
                <span className="jmi-chip-metric">💨 {weather.windSpeed} km/h</span>
              </>
            ) : (
              <span>Syncing district weather...</span>
            )}
          </div>
          
          {/* Greeting text just below weather chip - hidden on hover */}
          {!isHovering && (
            <div className="jmi-greeting-text-section">
              <div className="jmi-greeting-label">{greetingText}</div>
              {/* Buyer identity row: logo + name side-by-side */}
              <div className="jmi-identity-row">
              
                <h1 className="jmi-partner-title">{buyerName}</h1>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Quote Border - INSIDE the card as bottom border */}
        {selectedQuote && (
          <div className="jmi-quote-border-container">
            <div className="jmi-quote-border-content">
              <p className="jmi-quote-body">
                &ldquo;{selectedQuote.quote}&rdquo;{" "}
                <span className="jmi-quote-author">&mdash; {selectedQuote.author}</span>
              </p>
              <button
                type="button"
                onClick={handleRefreshQuote}
                className="jmi-quote-refresh-btn"
                title="Next ecosystem insight"
                aria-label="Refresh business insight"
              >
                ◆
              </button>
            </div>
          </div>
        )}
        
      </section>
      
      </div>
      {/* Embedded High-Performance Responsive CSS */}
      <style>{`
        .jmi-greeting-root {
          width: 100%;
          box-sizing: border-box;
        }

        .jmi-greeting-wrapper {
          position: relative;
          width: 100%;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 10px 32px rgba(0, 0, 0, 0.35);
        }

        .jmi-living-card {
          position: relative;
          width: 100%;
          min-height: 170px;
          background-color: #0b132b;
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1);
          user-select: none;
          display: flex;
          flex-direction: column;
        }

        .jmi-sky-bg {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
        }

        /* Geometric Shapes Background Layer */
        .jmi-geometric-shapes {
          position: absolute;
          inset: 0;
          z-index: 2;
          pointer-events: none;
          overflow: hidden;
        }

        .jmi-shape {
          position: absolute;
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.1));
          border: 1px solid rgba(251, 191, 36, 0.15);
        }

        .jmi-circle-1 {
          width: 180px;
          height: 180px;
          border-radius: 50%;
          top: 5%;
          right: 8%;
          animation: jmiFloatSlow 25s ease-in-out infinite;
        }

        .jmi-circle-2 {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          bottom: 35%;
          left: 12%;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(147, 197, 253, 0.08));
          border-color: rgba(59, 130, 246, 0.12);
          animation: jmiFloatSlow 30s ease-in-out infinite reverse;
        }

        .jmi-triangle-1 {
          width: 0;
          height: 0;
          border-left: 70px solid transparent;
          border-right: 70px solid transparent;
          border-bottom: 120px solid rgba(168, 85, 247, 0.1);
          background: none;
          border-top: none;
          top: 15%;
          left: 25%;
          animation: jmiRotateSlow 40s linear infinite;
        }

        .jmi-square-1 {
          width: 100px;
          height: 100px;
          top: 50%;
          right: 25%;
          background: linear-gradient(135deg, rgba(236, 72, 153, 0.12), rgba(219, 39, 119, 0.06));
          border-color: rgba(236, 72, 153, 0.1);
          transform: rotate(45deg);
          animation: jmiFloatSlow 35s ease-in-out infinite;
        }

        .jmi-hexagon-1 {
          width: 90px;
          height: 52px;
          background: rgba(16, 185, 129, 0.08);
          border: none;
          top: 8%;
          left: 45%;
          position: absolute;
          animation: jmiFloatSlow 28s ease-in-out infinite reverse;
        }
        
        .jmi-hexagon-1:before,
        .jmi-hexagon-1:after {
          content: "";
          position: absolute;
          width: 0;
          border-left: 45px solid transparent;
          border-right: 45px solid transparent;
        }
        
        .jmi-hexagon-1:before {
          bottom: 100%;
          border-bottom: 26px solid rgba(16, 185, 129, 0.08);
        }
        
        .jmi-hexagon-1:after {
          top: 100%;
          border-top: 26px solid rgba(16, 185, 129, 0.08);
        }

        @keyframes jmiFloatSlow {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(15px); }
        }

        @keyframes jmiRotateSlow {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .jmi-stars-layer {
          position: absolute;
          inset: 0;
          z-index: 2;
          pointer-events: none;
        }

        .jmi-diamond-star {
          position: absolute;
          background: #ffffff;
          border-radius: 50%;
          box-shadow: 0 0 6px rgba(255, 255, 255, 0.9);
          animation: jmiStarTwinkle 3s ease-in-out infinite alternate;
        }

        @keyframes jmiStarTwinkle {
          0% { opacity: 0.2; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.3); }
          100% { opacity: 0.3; transform: scale(0.9); }
        }

        .jmi-celestial-sun {
          position: absolute;
          width: 75px;
          height: 75px;
          border-radius: 50%;
          z-index: 2;
          pointer-events: none;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 0 45px rgba(251, 191, 36, 0.6);
        }

        .jmi-sun-core {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: radial-gradient(circle, #ffffff 0%, #fde047 60%, #f59e0b 100%);
        }

        .jmi-sunset-glow {
          position: absolute;
          bottom: 25px;
          right: 18%;
          width: 170px;
          height: 85px;
          border-radius: 170px 170px 0 0;
          z-index: 2;
          pointer-events: none;
          filter: blur(8px);
          opacity: 0.85;
        }

        .jmi-celestial-moon {
          position: absolute;
          top: 18px;
          right: 14%;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: radial-gradient(circle at 35% 35%, #ffffff 0%, #e2e8f0 60%, #94a3b8 100%);
          box-shadow: 0 0 28px rgba(255, 255, 255, 0.55);
          z-index: 2;
          pointer-events: none;
        }

        .jmi-moon-crater {
          position: absolute;
          background: rgba(148, 163, 184, 0.35);
          border-radius: 50%;
        }
        .jmi-moon-crater.c1 { width: 8px; height: 8px; top: 12px; left: 16px; }
        .jmi-moon-crater.c2 { width: 5px; height: 5px; top: 24px; left: 24px; }

        .jmi-clouds-svg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 70px;
          z-index: 3;
          pointer-events: none;
        }

        .jmi-clouds-drift {
          animation: jmiCloudMove 70s linear infinite;
        }

        @keyframes jmiCloudMove {
          0% { transform: translateX(0); }
          100% { transform: translateX(200px); }
        }

        /* --- Digital Hoarding Billboard Switching Animation --- */
        .jmi-billboard-frame-a {
          animation: jmiBillboardSwitchA 8s ease-in-out infinite;
          transform-origin: 430px 125px;
        }
        .jmi-billboard-frame-b {
          animation: jmiBillboardSwitchB 8s ease-in-out infinite;
          transform-origin: 430px 125px;
        }

        @keyframes jmiBillboardSwitchA {
          0%, 45% { opacity: 1; transform: scale(1); }
          50%, 95% { opacity: 0; transform: scale(0.96); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes jmiBillboardSwitchB {
          0%, 45% { opacity: 0; transform: scale(0.96); }
          50%, 95% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.96); }
        }

        /* --- Pure Glowing JMI Text (Glowing Red on Roof) --- */
        .jmi-pure-neon-text {
          animation: jmiPureRedGlowPulse 3s ease-in-out infinite alternate;
        }

        @keyframes jmiPureRedGlowPulse {
          0% {
            filter: drop-shadow(0 0 3px #ef4444) drop-shadow(0 0 8px rgba(239, 68, 68, 0.75));
            opacity: 0.94;
          }
          100% {
            filter: drop-shadow(0 0 5px #ff4d4d) drop-shadow(0 0 14px rgba(239, 68, 68, 0.95));
            opacity: 1;
          }
        }

        /* --- Birds Flock & Flapping Physics --- */
        .jmi-birds-flock {
          animation: jmiBirdsFly 26s linear infinite;
        }

        @keyframes jmiBirdsFly {
          0% { transform: translate(-80px, 35px); }
          100% { transform: translate(1280px, 20px); }
        }

        .jmi-bird-wing {
          animation: jmiBirdFlap 0.65s ease-in-out infinite alternate;
          transform-origin: center;
        }

        @keyframes jmiBirdFlap {
          0% { transform: scaleY(1); }
          100% { transform: scaleY(-0.6); }
        }

        /* --- Commercial Plane Physics --- */
        .jmi-commercial-plane-1 {
          animation: jmiCommercialPlane1Fly 45s linear infinite;
          animation-delay: 3s;
        }
        .jmi-commercial-plane-2 {
          animation: jmiCommercialPlane2Fly 50s linear infinite;
          animation-delay: 30s; /* 27 second gap between planes */
        }

        @keyframes jmiCommercialPlane1Fly {
          0% { transform: translate(-150px, 15px) scale(3.0) rotate(178deg); }
          100% { transform: translate(1450px, 15px) scale(3.0) rotate(178deg); }
        }
        @keyframes jmiCommercialPlane2Fly {
          0% { transform: translate(-120px, 20px) scale(3.0); }
          100% { transform: translate(1420px, 20px) scale(3.0); }
        }

        .jmi-weather-layer {
          position: absolute;
          inset: 0;
          z-index: 4;
          pointer-events: none;
          overflow: hidden;
        }

        .jmi-rain-streak {
          position: absolute;
          top: -20px;
          width: 1.5px;
          height: 16px;
          background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(186, 230, 253, 0.9) 100%);
          animation: jmiRainFall 0.5s linear infinite;
        }

        @keyframes jmiRainFall {
          0% { transform: translateY(0) skewX(-12deg); opacity: 0; }
          50% { opacity: 0.9; }
          100% { transform: translateY(280px) skewX(-12deg); opacity: 0.1; }
        }

        .jmi-snow-particle {
          position: absolute;
          top: -15px;
          color: #ffffff;
          opacity: 0.85;
          animation: jmiSnowFall 3.5s linear infinite;
        }

        @keyframes jmiSnowFall {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          30% { opacity: 1; }
          100% { transform: translateY(280px) translateX(25px); opacity: 0.2; }
        }

        .jmi-lightning-pulse {
          position: absolute;
          inset: 0;
          z-index: 4;
          pointer-events: none;
          background: #ffffff;
          opacity: 0;
          animation: jmiFlash 8s infinite;
        }

        @keyframes jmiFlash {
          0%, 94%, 98%, 100% { opacity: 0; }
          95% { opacity: 0.45; }
          96% { opacity: 0.1; }
          97% { opacity: 0.6; }
        }

        .jmi-fog-blanket {
          position: absolute;
          inset: 0;
          z-index: 4;
          pointer-events: none;
          background: linear-gradient(180deg, rgba(148, 163, 184, 0.1) 0%, rgba(203, 213, 225, 0.35) 100%);
          backdrop-filter: blur(1px);
        }

        .jmi-sparkle-layer {
          position: absolute;
          inset: 0;
          z-index: 5;
          pointer-events: none;
        }

        .jmi-sparkle-glint {
          position: absolute;
          color: #fde047;
          font-size: 11px;
          text-shadow: 0 0 8px rgba(254, 240, 138, 0.9);
          animation: jmiGlintPulse 4s ease-in-out infinite;
        }
        .jmi-sparkle-glint.s1 { top: 38%; left: 47%; animation-delay: 0.3s; }
        .jmi-sparkle-glint.s2 { top: 22%; left: 56%; animation-delay: 1.5s; font-size: 14px; color: #ffffff; }
        .jmi-sparkle-glint.s3 { top: 48%; left: 24%; animation-delay: 2.7s; }
        .jmi-sparkle-glint.s4 { top: 42%; left: 74%; animation-delay: 3.2s; }

        @keyframes jmiGlintPulse {
          0%, 100% { opacity: 0; transform: scale(0.4) rotate(0deg); }
          50% { opacity: 1; transform: scale(1.3) rotate(45deg); }
        }

        .jmi-tower-beacon {
          animation: jmiBeaconBlink 1.6s ease-in-out infinite;
        }

        @keyframes jmiBeaconBlink {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 1; }
        }

        .jmi-diamond-spire {
          animation: jmiBeaconBlink 2.2s ease-in-out infinite;
        }

        .jmi-cityscape-svg {
          position: absolute;
          bottom: 42px;
          left: 0;
          width: 100%;
          height: calc(100% - 42px);
          z-index: 3;
          pointer-events: none;
        }

        /* --- Vehicle & Pedestrian CSS Motion Physics --- */
        .jmi-truck-flow {
          animation: jmiTruckDrive 18s linear infinite;
        }

        @keyframes jmiTruckDrive {
          0% { transform: translateX(-120px); }
          100% { transform: translateX(1300px); }
        }

        .jmi-sportscar-flow {
          animation: jmiSportsCarDrive 11s linear infinite;
          animation-delay: 2s;
        }

        @keyframes jmiSportsCarDrive {
          0% { transform: translateX(-80px); }
          100% { transform: translateX(1290px); }
        }

        .jmi-motorcycle-flow {
          animation: jmiMotoDrive 14s linear infinite;
          animation-delay: 6s;
        }

        @keyframes jmiMotoDrive {
          0% { transform: translateX(-60px); }
          100% { transform: translateX(1280px); }
        }

        .jmi-sedan-flow {
          animation: jmiSedanDrive 16s linear infinite;
          animation-delay: 10s;
        }

        @keyframes jmiSedanDrive {
          0% { transform: translateX(-80px); }
          100% { transform: translateX(1290px); }
        }

        .jmi-police-patrol {
          animation: jmiPoliceDrive 22s linear infinite;
          animation-delay: 2s;
        }

        @keyframes jmiPoliceDrive {
          0% { transform: translateX(-70px); }
          100% { transform: translateX(1280px); }
        }

        /* Lamborghini exhaust fire particles */
        .jmi-exhaust-spark {
          animation: jmiExhaustFlicker 0.3s ease-in-out infinite;
        }
        .jmi-exhaust-spark.s1 { animation-delay: 0s; }
        .jmi-exhaust-spark.s2 { animation-delay: 0.1s; }
        .jmi-exhaust-spark.s3 { animation-delay: 0.2s; }

        @keyframes jmiExhaustFlicker {
          0%, 100% { opacity: 0.3; transform: scale(0.8) translateX(0); }
          50% { opacity: 1; transform: scale(1.3) translateX(-3px); }
        }

        .jmi-lambo-exhaust {
          animation: jmiExhaustPulse 0.4s ease-in-out infinite;
        }

        @keyframes jmiExhaustPulse {
          0%, 100% { opacity: 0.85; }
          50% { opacity: 1; }
        }

        .jmi-police-red {
          animation: jmiRedPulse 0.45s ease-in-out infinite alternate;
        }
        .jmi-police-blue {
          animation: jmiBluePulse 0.45s ease-in-out infinite alternate;
          animation-delay: 0.22s;
        }
        
        .jmi-police-red-reflect {
          animation: jmiRedPulse 0.45s ease-in-out infinite alternate;
        }
        .jmi-police-blue-reflect {
          animation: jmiBluePulse 0.45s ease-in-out infinite alternate;
          animation-delay: 0.22s;
        }

        @keyframes jmiRedPulse {
          0% { opacity: 0.2; }
          100% { opacity: 1; filter: drop-shadow(0 0 3px #ef4444); }
        }
        @keyframes jmiBluePulse {
          0% { opacity: 0.2; }
          100% { opacity: 1; filter: drop-shadow(0 0 3px #3b82f6); }
        }

        .jmi-jogger-fast {
          animation: jmiJogRun 16s linear infinite;
        }
        .jmi-jogger-med {
          animation: jmiJogRun 20s linear infinite;
          animation-delay: 6s;
        }

        @keyframes jmiJogRun {
          0% { transform: translateX(-30px); }
          100% { transform: translateX(1250px); }
        }

        .jmi-cyclist-1 {
          animation: jmiCycleRide 14s linear infinite;
          animation-delay: 1s;
        }

        @keyframes jmiCycleRide {
          0% { transform: translateX(-40px); }
          100% { transform: translateX(1260px); }
        }

        .jmi-walker-biz {
          animation: jmiWalkSlow 28s linear infinite;
          animation-delay: 3s;
        }
        .jmi-walker-rain {
          animation: jmiWalkSlow 24s linear infinite;
        }
        .jmi-walker-woman {
          animation: jmiWalkSlow 32s linear infinite;
          animation-delay: 8s;
        }
        .jmi-walker-couple {
          animation: jmiWalkSlow 38s linear infinite;
          animation-delay: 14s;
        }
        .jmi-walker-guard {
          animation: jmiWalkSlow 42s linear infinite;
          animation-delay: 5s;
        }

        /* Shoppers going to/from buyer building */
        .jmi-shopper-to-mall {
          animation: jmiShopperGoToMall 22s linear infinite;
          animation-delay: 2s;
        }
        .jmi-shopper-from-mall {
          animation: jmiShopperFromMall 24s linear infinite;
          animation-delay: 8s;
        }
        .jmi-shopper-from-mall-2 {
          animation: jmiShopperFromMall 26s linear infinite;
          animation-delay: 16s;
        }
        .jmi-shopper-to-mall-2 {
          animation: jmiShopperGoToMall 20s linear infinite;
          animation-delay: 10s;
        }
        .jmi-shopper-from-mall-3 {
          animation: jmiShopperFromMall 28s linear infinite;
          animation-delay: 24s;
        }

        @keyframes jmiWalkSlow {
          0% { transform: translateX(-20px); }
          100% { transform: translateX(1240px); }
        }

        /* Shopper going TO buyer mall (walks from right to left, stops at mall) */
        @keyframes jmiShopperGoToMall {
          0% { transform: translateX(1240px); }
          70% { transform: translateX(160px); }
          75%, 100% { transform: translateX(160px); opacity: 0; }
        }

        /* Shopper coming FROM buyer mall (starts at mall, walks right) */
        @keyframes jmiShopperFromMall {
          0%, 5% { transform: translateX(155px); opacity: 0; }
          10% { transform: translateX(155px); opacity: 1; }
          100% { transform: translateX(800px); opacity: 1; }
        }

        @keyframes jmiCoupleHeartPop {
          0%, 100% { opacity: 0; transform: scale(0.5) translateY(0); }
          50% { opacity: 1; transform: scale(1.4) translateY(-3px); }
        }
        .jmi-couple-heart {
          animation: jmiCoupleHeartPop 3.5s ease-in-out infinite;
        }

        /* --- Overlays & Typography --- */
        .jmi-top-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          padding: 12px 16px;
          z-index: 6;
          background: linear-gradient(180deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.45) 70%, rgba(0, 0, 0, 0) 100%);
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 10px;
        }

        .jmi-weather-chip {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 5px 12px;
          border-radius: 999px;
          background: rgba(15, 23, 42, 0.72);
          border: 1px solid rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          color: #e2e8f0;
          font-size: 0.76rem;
          font-weight: 600;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
        }

        .jmi-weather-icon-wrap {
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .jmi-chip-temp {
          color: #ffffff;
          font-weight: 700;
        }

        .jmi-chip-divider {
          opacity: 0.4;
          margin: 0 1px;
        }

        .jmi-chip-desc {
          color: #cbd5e1;
        }

        .jmi-chip-metric {
          opacity: 0.88;
        }

        .jmi-greeting-text-section {
          display: flex;
          flex-direction: column;
          gap: 6px;
          width: 100%;
        }

        .jmi-greeting-label {
          color: #fbbf24;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .jmi-identity-row {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .jmi-buyer-logo-chip {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          object-fit: contain;
          background: #ffffff;
          padding: 3px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
          flex-shrink: 0;
        }

        .jmi-partner-title {
          color: #ffffff;
          font-size: 20px;
          font-weight: 800;
          margin: 0;
          line-height: 1.15;
          letter-spacing: -0.02em;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.6);
        }

        .jmi-quote-border-container {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          width: 100%;
          height: 42px;
          z-index: 10;
          border-top: 2px solid rgba(251, 191, 36, 0.5);
          border-bottom: none;
          background: linear-gradient(90deg, rgba(2, 6, 23, 0.95) 0%, rgba(15, 23, 42, 0.92) 50%, rgba(2, 6, 23, 0.95) 100%);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 4px rgba(251, 191, 36, 0.15);
        }

        .jmi-quote-border-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 8px 14px;
        }

        .jmi-quote-body {
          color: #e2e8f0;
          font-size: 0.7rem;
          font-style: italic;
          margin: 0;
          line-height: 1.35;
          flex: 1;
          word-wrap: break-word;
          overflow-wrap: break-word;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
        }

        .jmi-quote-author {
          font-style: normal;
          color: #fbbf24;
          font-weight: 700;
          white-space: nowrap;
        }

        .jmi-quote-refresh-btn {
          width: 26px;
          height: 26px;
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(217, 119, 6, 0.88);
          border: 1px solid rgba(251, 191, 36, 0.7);
          border-radius: 6px;
          color: #ffffff;
          font-size: 0.75rem;
          cursor: pointer;
          transition: transform 0.15s ease, background 0.2s ease, box-shadow 0.2s ease;
          box-shadow: 0 2px 8px rgba(217, 119, 6, 0.35);
        }

        .jmi-quote-refresh-btn:hover {
          background: rgba(245, 158, 11, 1);
          transform: translateY(-1px) scale(1.05);
          box-shadow: 0 3px 12px rgba(245, 158, 11, 0.5);
        }

        .jmi-quote-refresh-btn:active {
          transform: translateY(0) scale(0.95);
        }

        /* --- Accessibility / Reduced Motion Mode --- */
        @media (prefers-reduced-motion: reduce) {
          .jmi-truck-flow,
          .jmi-sportscar-flow,
          .jmi-motorcycle-flow,
          .jmi-sedan-flow,
          .jmi-police-patrol,
          .jmi-lambo-flow,
          .jmi-birds-flock,
          .jmi-bird-wing,
          .jmi-commercial-plane-1,
          .jmi-commercial-plane-2,
          .jmi-jogger-fast,
          .jmi-jogger-med,
          .jmi-cyclist-1,
          .jmi-walker-biz,
          .jmi-walker-rain,
          .jmi-walker-woman,
          .jmi-walker-couple,
          .jmi-walker-guard,
          .jmi-shopper-to-mall,
          .jmi-shopper-from-mall,
          .jmi-shopper-from-mall-2,
          .jmi-shopper-to-mall-2,
          .jmi-shopper-from-mall-3,
          .jmi-clouds-drift,
          .jmi-rain-streak,
          .jmi-snow-particle,
          .jmi-diamond-star,
          .jmi-sparkle-glint,
          .jmi-billboard-frame-a,
          .jmi-billboard-frame-b,
          .jmi-pure-neon-text {
            animation: none !important;
          }
          .jmi-truck-flow {
            transform: translateX(450px) !important;
          }
          .jmi-sportscar-flow {
            transform: translateX(650px) !important;
          }
          .jmi-commercial-plane-1 {
            transform: translate(650px, 2px) scale(3.6) rotate(178deg) !important;
          }
          .jmi-commercial-plane-2 {
            transform: translate(800px, 10px) scale(2.2) !important;
          }
          .jmi-police-patrol {
            transform: translateX(200px) !important;
          }
          .jmi-lambo-flow {
            transform: translateX(750px) !important;
          }
          .jmi-shopper-to-mall {
            transform: translateX(180px) !important;
          }
          .jmi-shopper-from-mall {
            transform: translateX(220px) !important;
          }
          .jmi-shopper-from-mall-2 {
            transform: translateX(150px) !important;
          }
          .jmi-shopper-to-mall-2 {
            transform: translateX(100px) !important;
          }
          .jmi-shopper-from-mall-3 {
            transform: translateX(260px) !important;
          }
          .jmi-billboard-frame-a {
            opacity: 1 !important;
          }
        }

        /* --- Responsive Layout Tweaks --- */
        @media (max-width: 640px) {
          .jmi-living-card {
            min-height: 170px;
          }
          .jmi-weather-chip {
            font-size: 0.68rem;
            padding: 4px 10px;
            gap: 5px;
            flex-wrap: wrap;
            max-width: 280px;
          }
          .jmi-chip-metric {
            display: inline;
            opacity: 0.85;
            font-size: 0.64rem;
          }
          .jmi-greeting-label {
            font-size: 0.68rem;
          }
          .jmi-partner-title {
            font-size: 20px;
          }
          .jmi-buyer-logo-chip {
            width: 32px;
            height: 32px;
          }
          .jmi-quote-border-content {
            padding: 7px 12px;
            gap: 8px;
          }
          .jmi-quote-body {
            font-size: 0.65rem;
            line-height: 1.3;
          }
          .jmi-quote-refresh-btn {
            width: 24px;
            height: 24px;
            font-size: 0.7rem;
          }
        }

        /* --- Mall Door Opening/Closing Animation --- */
        .jmi-door-left {
          animation: jmiDoorOpenLeft 6s ease-in-out infinite;
          transform-origin: left center;
        }
        .jmi-door-right {
          animation: jmiDoorOpenRight 6s ease-in-out infinite;
          transform-origin: right center;
        }

        @keyframes jmiDoorOpenLeft {
          0%, 20% { transform: scaleX(1); opacity: 1; }
          30%, 70% { transform: scaleX(0.1); opacity: 0.3; }
          80%, 100% { transform: scaleX(1); opacity: 1; }
        }
        @keyframes jmiDoorOpenRight {
          0%, 20% { transform: scaleX(1); opacity: 1; }
          30%, 70% { transform: scaleX(0.1); opacity: 0.3; }
          80%, 100% { transform: scaleX(1); opacity: 1; }
        }

        /* --- Lamborghini Sometimes Stops Near Buyer Shop --- */
        .jmi-lambo-flow {
          animation: jmiLamboDriveWithStop 18s ease-in-out infinite;
          animation-delay: 4s;
        }

        @keyframes jmiLamboDriveWithStop {
          0% { transform: translateX(-80px); }
          20% { transform: translateX(100px); }      /* Approaching buyer shop */
          30%, 50% { transform: translateX(120px); }  /* STOPPED near buyer shop */
          70% { transform: translateX(600px); }       /* Driving away */
          100% { transform: translateX(1300px); }
        }

      `}</style>
    </div>
  );
};

export default Greeting;